import type { SVGProps } from 'react'
import { LocaleEnum } from '@/Shared/AutoGenerated/LocalizationEnum'

export interface CountryFlagProps extends SVGProps<SVGSVGElement> {
    locale: LocaleEnum
    title?: string
}

export function CountryFlag({
    locale,
    className = '',
    ...props
}: CountryFlagProps) {
    const hasAccessibleName =
        Boolean(props['aria-label']) ||
        Boolean(props['aria-labelledby']) ||
        Boolean(props.title)

    if (!hasAccessibleName && props['aria-hidden'] === undefined) {
        props['aria-hidden'] = 'true'
    }

    if (
        props.tabIndex === undefined &&
        (props as any).focusable === undefined
    ) {
        ;(props as any).focusable = 'false'
    }

    switch (locale) {
        case LocaleEnum.English:
            return (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    id="flag-icons-us"
                    viewBox="0 0 640 480"
                    className={className}
                    {...props}
                >
                    <path
                        fill="#bd3d44"
                        d="M0 0h640v480H0"
                    />
                    <path
                        stroke="#fff"
                        strokeWidth="37"
                        d="M0 55.3h640M0 129h640M0 203h640M0 277h640M0 351h640M0 425h640"
                    />
                    <path
                        fill="#192f5d"
                        d="M0 0h364.8v258.5H0"
                    />
                    <marker
                        id="us-a"
                        markerHeight="30"
                        markerWidth="30"
                    >
                        <path
                            fill="#fff"
                            d="m14 0 9 27L0 10h28L5 27z"
                        />
                    </marker>
                    <path
                        fill="none"
                        markerMid="url(#us-a)"
                        d="m0 0 16 11h61 61 61 61 60L47 37h61 61 60 61L16 63h61 61 61 61 60L47 89h61 61 60 61L16 115h61 61 61 61 60L47 141h61 61 60 61L16 166h61 61 61 61 60L47 192h61 61 60 61L16 218h61 61 61 61 60z"
                    />
                </svg>
            )
        case LocaleEnum.Polish:
            return (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    id="flag-icons-pl"
                    viewBox="0 0 640 480"
                    className={className}
                    {...props}
                >
                    <g fillRule="evenodd">
                        <path
                            fill="#fff"
                            d="M640 480H0V0h640z"
                        />
                        <path
                            fill="#dc143c"
                            d="M640 480H0V240h640z"
                        />
                    </g>
                </svg>
            )
        case LocaleEnum.Spanish:
            return (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    id="flag-icons-es"
                    viewBox="0 0 640 480"
                    className={className}
                    {...props}
                >
                    <path
                        fill="#AA151B"
                        d="M0 0h640v480H0z"
                    />
                    <path
                        fill="#F1BF00"
                        d="M0 120h640v240H0z"
                    />
                    <path
                        fill="#ad1519"
                        d="m127.3 213.3-.8-.1-1-1-.7-.4-.6-.8s-.7-1.1-.4-2q.5-1.2 1.4-1.5l1.5-.5 1-.4 1.3-.3.5-.3 1-.2 1-.2 1.6.1h4.8c.4 0 1.2.3 1.4.4l2 .7c.5.1 1.6.3 2.2.6q.8.5 1.1 1l.5 1v1.1l-.5.8-.6 1-.8.6s-.5.5-1 .4-4.8-.8-7.6-.8-7.3.9-7.3.9"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".3"
                        d="m127.3 213.3-.8-.1-1-1-.7-.4-.6-.8s-.7-1.1-.4-2q.5-1.2 1.4-1.5l1.5-.5 1-.4 1.3-.3.5-.3 1-.2 1-.2 1.6.1h4.8c.4 0 1.2.3 1.4.4l2 .7c.5.1 1.6.3 2.2.6q.8.5 1.1 1l.5 1v1.1l-.5.8-.6 1-.8.6s-.5.5-1 .4-4.8-.8-7.6-.8-7.3.9-7.3.9z"
                    />
                    <path
                        fill="#c8b100"
                        d="M133.3 207q.1-2.1 1.3-2.3 1.3.1 1.4 2.4c0 1.3-.6 2.4-1.4 2.4s-1.3-1.1-1.3-2.5"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M133.3 207q.1-2.1 1.3-2.3 1.3.1 1.4 2.4c0 1.3-.6 2.4-1.4 2.4s-1.3-1.1-1.3-2.5z"
                    />
                    <path
                        fill="#c8b100"
                        d="M134 207q0-2 .7-2.1c.3 0 .6 1 .6 2.1q0 2-.6 2.2c-.4 0-.6-1-.6-2.2"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M134 207q0-2 .7-2.1c.3 0 .6 1 .6 2.1q0 2-.6 2.2c-.4 0-.6-1-.6-2.2z"
                    />
                    <path
                        fill="#c8b100"
                        d="M133.8 204.5q.1-.7.8-.8 1 0 1 .8-.1.8-1 .9c-.9.1-.8-.4-.8-.9"
                    />
                    <path
                        fill="#c8b100"
                        d="M135.3 204.2v.6h-1.4v-.6h.5V203h-.7v-.6h.7v-.5h.5v.5h.6v.6h-.6v1.2z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M135.3 204.2v.6h-1.4v-.6h.5V203h-.7v-.6h.7v-.5h.5v.5h.6v.6h-.6v1.2h.4"
                    />
                    <path
                        fill="#c8b100"
                        d="M135.9 204.2v.6h-2.5v-.6h1V203h-.7v-.6h.7v-.5h.5v.5h.6v.6h-.6v1.2z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M135.9 204.2v.6h-2.5v-.6h1V203h-.7v-.6h.7v-.5h.5v.5h.6v.6h-.6v1.2h1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M134.9 203.7q.6.2.6.8-.1.8-.8.9-1 0-1-.9 0-.6.7-.8"
                    />
                    <path
                        fill="#c8b100"
                        d="M134.7 213.2H130v-1.1l-.3-1.2-.2-1.5c-1.3-1.7-2.5-2.8-2.9-2.5q.1-.5.5-.7c1.1-.7 3.5 1 5.2 3.6l.5.7h3.8l.4-.7c1.8-2.7 4.1-4.3 5.2-3.6q.4.2.5.7c-.4-.3-1.6.8-2.9 2.5l-.2 1.5-.2 1.2-.1 1.1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M134.7 213.2H130v-1.1l-.3-1.2-.2-1.5c-1.3-1.7-2.5-2.8-2.9-2.5q.1-.5.5-.7c1.1-.7 3.5 1 5.2 3.6l.5.7h3.8l.4-.7c1.8-2.7 4.1-4.3 5.2-3.6q.4.2.5.7c-.4-.3-1.6.8-2.9 2.5l-.2 1.5-.2 1.2-.1 1.1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M126.8 206.8c1-.5 3 1.1 4.6 3.6m11-3.6c-.8-.5-2.8 1.1-4.5 3.6"
                    />
                    <path
                        fill="#c8b100"
                        d="m127.8 215.3-.5-1a27 27 0 0 1 14.7 0l-.5.8-.3.8a23 23 0 0 0-6.6-.8c-2.6 0-5.2.3-6.5.8z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m127.8 215.3-.5-1a27 27 0 0 1 14.7 0l-.5.8-.3.8a23 23 0 0 0-6.6-.8c-2.6 0-5.2.3-6.5.8l-.3-.6"
                    />
                    <path
                        fill="#c8b100"
                        d="M134.6 217.7c2.4 0 5-.4 5.9-.6q1-.3 1-.8 0-.3-.4-.4c-1.4-.5-4-.8-6.5-.8s-5 .3-6.4.8q-.3 0-.4.3 0 .6 1 .9c1 .2 3.5.6 5.8.6"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M134.6 217.7c2.4 0 5-.4 5.9-.6q1-.3 1-.8 0-.3-.4-.4c-1.4-.5-4-.8-6.5-.8s-5 .3-6.4.8q-.3 0-.4.3 0 .6 1 .9c1 .2 3.5.6 5.8.6z"
                    />
                    <path
                        fill="#c8b100"
                        d="m142.1 213.2-.5-.5s-.6.3-1.3.2c-.6 0-.9-1-.9-1s-.7.7-1.3.7-1-.6-1-.6-.7.5-1.3.4-1.2-.8-1.2-.8-.6.8-1.2.8c-.6.1-1-.5-1-.5s-.4.6-1.1.7-1.4-.6-1.4-.6-.5.7-1 1c-.5 0-1.2-.4-1.2-.4l-.2.5-.3.1.2.5a27 27 0 0 1 7.2-.9q4.5 0 7.4 1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m142.1 213.2-.5-.5s-.6.3-1.3.2c-.6 0-.9-1-.9-1s-.7.7-1.3.7-1-.6-1-.6-.7.5-1.3.4-1.2-.8-1.2-.8-.6.8-1.2.8c-.6.1-1-.5-1-.5s-.4.6-1.1.7-1.4-.6-1.4-.6-.5.7-1 1c-.5 0-1.2-.4-1.2-.4l-.2.5-.3.1.2.5a27 27 0 0 1 7.2-.9q4.5 0 7.4 1z"
                    />
                    <path
                        fill="#c8b100"
                        d="M134.7 210.7h.2v.4q0 1 1 1a1 1 0 0 0 1-.7l.2-.3v.4q.3.8 1.1.8 1 0 1-1v-.1l.4-.4.2.5-.1.4a1 1 0 0 0 1 1q.6 0 .9-.5l.2-.2v.3q0 .5.4.7l1-.4.7-.7v.4s-.5.8-1 1q-.3.4-.8.3-.5 0-.7-.6-.3.3-.7.2-1 0-1.4-.8-.5.5-1.1.5a2 2 0 0 1-1.2-.6l-1 .4a2 2 0 0 1-1.3-.6 1.6 1.6 0 0 1-2.4.2 2 2 0 0 1-1.2.6l-1.1-.5q-.4.8-1.4.8l-.7-.2q-.2.5-.7.6t-.9-.2l-1-1 .1-.5.8.7q.8.5.9.4.4-.1.4-.7v-.3l.2.2q.3.5.9.5a1 1 0 0 0 1-1v-.9l.4.4v.1q.1 1 1 1t1.1-.9v-.3l.2.3q.3.6 1 .7 1 0 1.1-1v-.3h.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M134.7 210.7h.2v.4q0 1 1 1a1 1 0 0 0 1-.7l.2-.3v.4q.3.8 1.1.8 1 0 1-1v-.1l.4-.4.2.5-.1.4a1 1 0 0 0 1 1q.6 0 .9-.5l.2-.2v.3q0 .5.4.7l1-.4.7-.7v.4s-.5.8-1 1q-.3.4-.8.3-.5 0-.7-.6-.3.3-.7.2-1 0-1.4-.8-.5.5-1.1.5a2 2 0 0 1-1.2-.6l-1 .4a2 2 0 0 1-1.3-.6 1.6 1.6 0 0 1-2.4.2 2 2 0 0 1-1.2.6l-1.1-.5q-.4.8-1.4.8l-.7-.2q-.2.5-.7.6t-.9-.2l-1-1 .1-.5.8.7q.8.5.9.4.4-.1.4-.7v-.3l.2.2q.3.5.9.5a1 1 0 0 0 1-1v-.9l.4.4v.1q.1 1 1 1t1.1-.9v-.3l.2.3q.3.6 1 .7 1 0 1.1-1v-.3h.3z"
                    />
                    <path
                        fill="#c8b100"
                        d="M134.6 213.3q-4.5 0-7.3 1l-.3-.2.1-.3a27 27 0 0 1 7.5-1q4.6 0 7.6 1l.1.3-.3.2a27 27 0 0 0-7.4-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".3"
                        d="M134.6 213.3q-4.5 0-7.3 1l-.3-.2.1-.3a27 27 0 0 1 7.5-1q4.6 0 7.6 1l.1.3-.3.2a27 27 0 0 0-7.4-1z"
                    />
                    <path
                        fill="#fff"
                        d="M131.8 214.4q0-.4.5-.4a.4.4 0 0 1 .4.4q0 .3-.4.4a.4.4 0 0 1-.5-.4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M131.8 214.4q0-.4.5-.4a.4.4 0 0 1 .4.4q0 .3-.4.4a.4.4 0 0 1-.5-.4z"
                    />
                    <path
                        fill="#ad1519"
                        d="M134.7 214.5h-1q-.2 0-.3-.3l.3-.3h2l.2.3-.3.3h-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M134.7 214.5h-1q-.2 0-.3-.3l.3-.3h2l.2.3-.3.3h-1"
                    />
                    <path
                        fill="#058e6e"
                        d="M130 214.9h-.7q-.2 0-.3-.2l.2-.3.7-.1.7-.1q.3 0 .4.2a.3.3 0 0 1-.3.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M130 214.9h-.7q-.2 0-.3-.2l.2-.3.7-.1.7-.1q.3 0 .4.2a.3.3 0 0 1-.3.4h-.7"
                    />
                    <path
                        fill="#ad1519"
                        d="m127.3 215.3.3-.4h.7l-.4.6z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m127.3 215.3.3-.4h.7l-.4.6-.6-.2"
                    />
                    <path
                        fill="#fff"
                        d="M136.6 214.4q0-.4.4-.4a.4.4 0 0 1 .5.4.4.4 0 0 1-.5.4.4.4 0 0 1-.4-.4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M136.6 214.4q0-.4.4-.4a.4.4 0 0 1 .5.4.4.4 0 0 1-.5.4.4.4 0 0 1-.4-.4z"
                    />
                    <path
                        fill="#058e6e"
                        d="M139.3 214.9h.6a.3.3 0 0 0 .4-.2l-.3-.3-.6-.1-.7-.1q-.3 0-.4.2 0 .3.3.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M139.3 214.9h.6a.3.3 0 0 0 .4-.2l-.3-.3-.6-.1-.7-.1q-.3 0-.4.2 0 .3.3.4h.7"
                    />
                    <path
                        fill="#ad1519"
                        d="m142 215.4-.3-.5h-.7l.3.6z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m142 215.4-.3-.5h-.7l.3.6.6-.1"
                    />
                    <path
                        fill="#ad1519"
                        d="M134.6 217.1a25 25 0 0 1-6-.6 26 26 0 0 1 12.1 0q-2.5.6-6 .6"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".3"
                        d="M134.6 217.1a25 25 0 0 1-6-.6 26 26 0 0 1 12.1 0q-2.5.6-6 .6z"
                    />
                    <path
                        fill="#c8b100"
                        d="m142 212-.1-.3q-.3 0-.4.2 0 .4.2.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m142 212-.1-.3q-.3 0-.4.2 0 .4.2.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="M137.3 211.2q0-.3-.2-.4l-.2.3q0 .3.2.4l.3-.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M137.3 211.2q0-.3-.2-.4l-.2.3q0 .3.2.4l.3-.3z"
                    />
                    <path
                        fill="#c8b100"
                        d="m132 211.2.1-.4q.3 0 .3.3t-.2.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m132 211.2.1-.4q.3 0 .3.3t-.2.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="m127.3 212 .1-.3q.3 0 .4.2 0 .4-.2.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m127.3 212 .1-.3q.3 0 .4.2 0 .4-.2.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="m134.6 208.5-.8.5.6 1.3.2.1.2-.1.7-1.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m134.6 208.5-.8.5.6 1.3.2.1.2-.1.7-1.3-.9-.5"
                    />
                    <path
                        fill="#c8b100"
                        d="m132.8 210.5.4.5 1.3-.4.1-.2-.1-.2-1.3-.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m132.8 210.5.4.5 1.3-.4.1-.2-.1-.2-1.3-.3-.4.6"
                    />
                    <path
                        fill="#c8b100"
                        d="m136.4 210.5-.3.5-1.3-.4-.2-.2.2-.2 1.3-.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m136.4 210.5-.3.5-1.3-.4-.2-.2.2-.2 1.3-.3.3.6"
                    />
                    <path
                        fill="#c8b100"
                        d="m129.3 209-.7.7.9 1 .2.1.1-.1.3-1.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m129.3 209-.7.7.9 1 .2.1.1-.1.3-1.3-.8-.3"
                    />
                    <path
                        fill="#c8b100"
                        d="m128 211.2.4.5 1.2-.6v-.2l-.1-.2-1.3-.1-.3.6"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m128 211.2.4.5 1.2-.6v-.2l-.1-.2-1.3-.1-.3.6"
                    />
                    <path
                        fill="#c8b100"
                        d="m131.5 210.5-.3.6H130l-.2-.2.1-.3 1.2-.6z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m131.5 210.5-.3.6H130l-.2-.2.1-.3 1.2-.6.5.5"
                    />
                    <path
                        fill="#c8b100"
                        d="M126.6 211.4v.6l-1.4.2-.2-.1v-.2l1-.9z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M126.6 211.4v.6l-1.4.2-.2-.1v-.2l1-.9.6.4"
                    />
                    <path
                        fill="#c8b100"
                        d="M129.2 210.9q0-.5.5-.5t.5.5l-.5.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M129.2 210.9q0-.5.5-.5t.5.5l-.5.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="m140 209 .7.7-.9 1-.2.1-.1-.1-.3-1.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m140 209 .7.7-.9 1-.2.1-.1-.1-.3-1.3.8-.3"
                    />
                    <path
                        fill="#c8b100"
                        d="m141.4 211.2-.5.5-1.2-.6v-.2l.1-.2 1.3-.1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m141.4 211.2-.5.5-1.2-.6v-.2l.1-.2 1.3-.1.3.6"
                    />
                    <path
                        fill="#c8b100"
                        d="m137.8 210.5.3.6h1.3l.2-.2-.1-.3-1.2-.6z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m137.8 210.5.3.6h1.3l.2-.2-.1-.3-1.2-.6-.5.5"
                    />
                    <path
                        fill="#c8b100"
                        d="m142.5 211.4.1.6 1.3.2.2-.1v-.2l-1-.9z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m142.5 211.4.1.6 1.3.2.2-.1v-.2l-1-.9-.6.4"
                    />
                    <path
                        fill="#c8b100"
                        d="m134.2 210.4.4-.4q.5 0 .5.4l-.5.5z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m134.2 210.4.4-.4q.5 0 .5.4l-.5.5z"
                    />
                    <path
                        fill="#c8b100"
                        d="M139.1 210.9q.1-.5.5-.5l.5.5-.5.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M139.1 210.9q.1-.5.5-.5l.5.5-.5.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="m124.8 212.2-.6-.7c-.2-.2-.7-.3-.7-.3q0-.2.6-.3l.4.2v-.2s.3 0 .4.3v1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m124.8 212.2-.6-.7c-.2-.2-.7-.3-.7-.3q0-.2.6-.3l.4.2v-.2s.3 0 .4.3v1z"
                    />
                    <path
                        fill="#c8b100"
                        d="M124.8 212q.3-.3.5 0t.2.5l-.5-.1q-.3-.2-.2-.5"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M124.8 212q.3-.3.5 0t.2.5l-.5-.1q-.3-.2-.2-.5z"
                    />
                    <path
                        fill="#c8b100"
                        d="m144.3 212.2.6-.7.7-.3q0-.2-.6-.3l-.4.2v-.2s-.3 0-.4.3v.7z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m144.3 212.2.6-.7.7-.3q0-.2-.6-.3l-.4.2v-.2s-.3 0-.4.3v.7z"
                    />
                    <path
                        fill="#c8b100"
                        d="M144.3 212q-.1-.3-.5 0-.3.2-.1.5l.5-.1q.3-.2.1-.5"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M144.3 212q-.1-.3-.5 0-.3.2-.1.5l.5-.1q.3-.2.1-.5z"
                    />
                    <path
                        fill="#c8b100"
                        d="M124 223h21.4v-5.5H124z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M124 223h21.4v-5.5H124z"
                    />
                    <path
                        fill="#c8b100"
                        d="M126.2 226.8h16.9a1.4 1.4 0 0 1-1-1.2q.1-1 1-1.3h-16.9q1 .3 1 1.3a1.3 1.3 0 0 1-1 1.2"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".4"
                        d="M126.2 226.8h16.9a1.4 1.4 0 0 1-1-1.2q.1-1 1-1.3h-16.9q1 .3 1 1.3a1.3 1.3 0 0 1-1 1.2z"
                    />
                    <path
                        fill="#c8b100"
                        d="M126.6 226.8h16q1 0 1 .7t-1 .8h-16q-.9-.1-1-.8 0-1 1-.8"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M126.6 226.8h16q1 0 1 .7t-1 .8h-16q-.9-.1-1-.8 0-1 1-.8z"
                    />
                    <path
                        fill="#c8b100"
                        d="M126.6 223h16q1 .1 1 .7t-1 .6h-16q-.9 0-1-.6.1-.5 1-.6"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M126.6 223h16q1 .1 1 .7t-1 .6h-16q-.9 0-1-.6.1-.5 1-.6z"
                    />
                    <path
                        fill="#005bbf"
                        d="M149.6 317.4q-2.2 0-3.7-.8a8 8 0 0 0-3.8-.8q-2.1 0-3.7.8a8 8 0 0 1-3.8.8q-2.3 0-3.7-.8a8 8 0 0 0-3.7-.8 8 8 0 0 0-3.7.8 8 8 0 0 1-3.8.8v2.4q2.3 0 3.8-.9a8 8 0 0 1 3.7-.8q2.2 0 3.7.8c1.5.8 2.2.9 3.7.9a8 8 0 0 0 3.8-.9q1.6-.8 3.7-.8 2.3 0 3.8.8c1.5.8 2.2.9 3.7.9z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M149.6 317.4q-2.2 0-3.7-.8a8 8 0 0 0-3.8-.8q-2.1 0-3.7.8a8 8 0 0 1-3.8.8q-2.3 0-3.7-.8a8 8 0 0 0-3.7-.8 8 8 0 0 0-3.7.8 8 8 0 0 1-3.8.8v2.4q2.3 0 3.8-.9a8 8 0 0 1 3.7-.8q2.2 0 3.7.8c1.5.8 2.2.9 3.7.9a8 8 0 0 0 3.8-.9q1.6-.8 3.7-.8 2.3 0 3.8.8c1.5.8 2.2.9 3.7.9z"
                    />
                    <path
                        fill="#ccc"
                        d="M149.6 319.8a8 8 0 0 1-3.7-.9 8 8 0 0 0-3.8-.8q-2.1 0-3.7.8c-1.6.8-2.3.9-3.8.9s-2.8-.4-3.7-.9a8 8 0 0 0-3.7-.8 8 8 0 0 0-3.7.8q-1.5.8-3.8.9v2.3q2.3 0 3.8-.9a8 8 0 0 1 3.7-.7q2.2 0 3.7.7a8 8 0 0 0 7.5 0 9 9 0 0 1 7.5.1 8 8 0 0 0 3.7.8z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M149.6 319.8a8 8 0 0 1-3.7-.9 8 8 0 0 0-3.8-.8q-2.1 0-3.7.8c-1.6.8-2.3.9-3.8.9s-2.8-.4-3.7-.9a8 8 0 0 0-3.7-.8 8 8 0 0 0-3.7.8q-1.5.8-3.8.9v2.3q2.3 0 3.8-.9a8 8 0 0 1 3.7-.7q2.2 0 3.7.7a8 8 0 0 0 7.5 0 9 9 0 0 1 7.5.1 8 8 0 0 0 3.7.8v-2.3"
                    />
                    <path
                        fill="#005bbf"
                        d="M149.6 322a7 7 0 0 1-3.7-.8 8 8 0 0 0-3.8-.7q-2.1 0-3.7.7-1.5.9-3.8.9c-2.3 0-2.8-.4-3.7-.9a8 8 0 0 0-3.7-.8 8 8 0 0 0-3.7.8q-1.5.8-3.8.9v2.3q2.3 0 3.8-.9a10 10 0 0 1 7.4 0 7 7 0 0 0 3.7.9 8 8 0 0 0 3.8-.8q1.6-.8 3.7-.8 2.3 0 3.8.8c1.5.8 2.2.8 3.7.8z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M149.6 322a7 7 0 0 1-3.7-.8 8 8 0 0 0-3.8-.7q-2.1 0-3.7.7-1.5.9-3.8.9c-2.3 0-2.8-.4-3.7-.9a8 8 0 0 0-3.7-.8 8 8 0 0 0-3.7.8q-1.5.8-3.8.9v2.3q2.3 0 3.8-.9a10 10 0 0 1 7.4 0 7 7 0 0 0 3.7.9 8 8 0 0 0 3.8-.8q1.6-.8 3.7-.8 2.3 0 3.8.8c1.5.8 2.2.8 3.7.8V322"
                    />
                    <path
                        fill="#ccc"
                        d="M149.6 326.7a8 8 0 0 1-3.7-.8q-1.6-.8-3.7-.8a8 8 0 0 0-3.8.8q-1.5.8-3.8.8a7 7 0 0 1-3.7-.9 8 8 0 0 0-3.7-.7q-2.2 0-3.7.8c-1.5.8-2.3.8-3.8.8v-2.3a8 8 0 0 0 3.8-.9 10 10 0 0 1 7.4 0 8 8 0 0 0 3.7.9 8 8 0 0 0 3.8-.8q1.5-.8 3.8-.8t3.7.8c1.5.8 2.3.8 3.7.8z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M149.6 326.7a8 8 0 0 1-3.7-.8q-1.6-.8-3.7-.8a8 8 0 0 0-3.8.8q-1.5.8-3.8.8a7 7 0 0 1-3.7-.9 8 8 0 0 0-3.7-.7q-2.2 0-3.7.8c-1.5.8-2.3.8-3.8.8v-2.3a8 8 0 0 0 3.8-.9 10 10 0 0 1 7.4 0 8 8 0 0 0 3.7.9 8 8 0 0 0 3.8-.8q1.5-.8 3.8-.8t3.7.8c1.5.8 2.3.8 3.7.8v2.3"
                    />
                    <path
                        fill="#005bbf"
                        d="M149.6 329a8 8 0 0 1-3.7-.8q-1.6-.8-3.7-.8a8 8 0 0 0-3.8.8q-1.5.8-3.8.8a7 7 0 0 1-3.7-.9 8 8 0 0 0-3.7-.7q-2.2 0-3.7.8c-1.5.8-2.3.8-3.8.8v-2.3a8 8 0 0 0 3.8-.8q1.5-.8 3.7-.8t3.7.7a8 8 0 0 0 7.5 0q1.5-.6 3.8-.7t3.7.8c1.5.8 2.2.8 3.7.8z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M149.6 329a8 8 0 0 1-3.7-.8q-1.6-.8-3.7-.8a8 8 0 0 0-3.8.8q-1.5.8-3.8.8a7 7 0 0 1-3.7-.9 8 8 0 0 0-3.7-.7q-2.2 0-3.7.8c-1.5.8-2.3.8-3.8.8v-2.3a8 8 0 0 0 3.8-.8q1.5-.8 3.7-.8t3.7.7a8 8 0 0 0 7.5 0q1.5-.6 3.8-.7t3.7.8c1.5.8 2.2.8 3.7.8z"
                    />
                    <path
                        fill="#c8b100"
                        d="m126.2 308 .2.5c0 1.5-1.3 2.6-2.7 2.6h22a3 3 0 0 1-2.7-2.6v-.5z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".4"
                        d="m126.2 308 .2.5c0 1.5-1.3 2.6-2.7 2.6h22a3 3 0 0 1-2.7-2.6v-.5z"
                    />
                    <path
                        fill="#c8b100"
                        d="M126.6 306.5h16q1 0 1 .8 0 .6-1 .7h-16q-.9 0-1-.8.1-.6 1-.7"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M126.6 306.5h16q1 0 1 .8 0 .6-1 .7h-16q-.9 0-1-.8.1-.6 1-.7z"
                    />
                    <path
                        fill="#c8b100"
                        d="M123.7 316.7h22V311h-22z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M123.7 316.7h22V311h-22z"
                    />
                    <path
                        fill="#ad1519"
                        d="M122 286.7c-2.2 1.2-3.7 2.5-3.4 3.2q.2.8 1.8 1.6c1.5 1.1 2.5 3 1.7 4a5.5 5.5 0 0 0-.1-8.8"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M122 286.7c-2.2 1.2-3.7 2.5-3.4 3.2q.2.8 1.8 1.6c1.5 1.1 2.5 3 1.7 4a5.5 5.5 0 0 0-.1-8.8z"
                    />
                    <path
                        fill="#ccc"
                        d="M126.8 305.6h15.6V229h-15.6v76.5z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M138 229.2v76.3m1.7-76.3v76.3m-12.9 0h15.6v-76.4h-15.6v76.5z"
                    />
                    <path
                        fill="#ad1519"
                        d="M158.4 257.7a50 50 0 0 0-23.3-2c-9.4 1.6-16.5 5.3-15.9 8.4v.2l-3.5-8.2c-.6-3.3 7.2-7.5 17.6-9.2a43 43 0 0 1 9.2-.7c6.6 0 12.4.8 15.8 2.1v9.4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".4"
                        d="M158.4 257.7a50 50 0 0 0-23.3-2c-9.4 1.6-16.5 5.3-15.9 8.4v.2l-3.5-8.2c-.6-3.3 7.2-7.5 17.6-9.2a43 43 0 0 1 9.2-.7c6.6 0 12.4.8 15.8 2.1v9.4"
                    />
                    <path
                        fill="#ad1519"
                        d="M126.8 267.3c-4.3-.3-7.3-1.4-7.6-3.2q-.3-2.2 3.8-4.5l3.8.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M126.8 267.3c-4.3-.3-7.3-1.4-7.6-3.2q-.3-2.2 3.8-4.5l3.8.3v7.4"
                    />
                    <path
                        fill="#ad1519"
                        d="M142.5 261.5q4.1.6 5.7 1.9l.1.2c.5 1-1.9 3-5.9 5.4v-7.5"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M142.5 261.5q4.1.6 5.7 1.9l.1.2c.5 1-1.9 3-5.9 5.4v-7.5"
                    />
                    <path
                        fill="#ad1519"
                        d="M117.1 282c-.4-1.2 3.8-3.6 9.8-5.8l7.8-3.2c8.3-3.7 14.4-7.9 13.6-9.4v-.2c.4.4 1 8 1 8 .8 1.3-4.8 5.5-12.4 9.1-2.5 1.2-7.6 3-10 4-4.4 1.4-8.7 4.3-8.3 5.3l-1.5-7.7"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".4"
                        d="M117.1 282c-.4-1.2 3.8-3.6 9.8-5.8l7.8-3.2c8.3-3.7 14.4-7.9 13.6-9.4v-.2c.4.4 1 8 1 8 .8 1.3-4.8 5.5-12.4 9.1-2.5 1.2-7.6 3-10 4-4.4 1.4-8.7 4.3-8.3 5.3l-1.5-7.7z"
                    />
                    <path
                        fill="#c8b100"
                        d="M125.8 254c1.9-.6 3.1-1.5 2.5-3q-.6-1.3-2.8-.6l-2.6 1 2.3 5.8.8-.3.8-.3zm-1.2-2.7.7-.3q1-.3 1.4.8.4.8-.5 1.5l-.6.3zm7.3-2.5-.9.3h-.8l1.3 6.1 4.3-.8-.2-.4v-.4l-2.5.6zm8.4 5.2q1.2-3.3 2.7-6.4h-1l-1.8 4.6-2.4-4.3-1 .1h-1l3.5 6zm8.8-4.7.4-.9a3 3 0 0 0-1.7-.6q-2.6 0-2.8 1.7c-.2 2.1 3.2 2 3 3.4q-.2 1-1.4.8-1.3 0-1.4-1.2h-.3l-.4 1.1a4 4 0 0 0 1.8.6q2.7.2 3.2-1.7c.2-2-3.3-2.1-3.1-3.4q0-.8 1.3-.7 1 0 1.2.9z"
                    />
                    <path
                        fill="#ad1519"
                        d="M277.9 211.6s-.7.8-1.3.9c-.5 0-1.1-.5-1.1-.5s-.5.5-1 .6-1.4-.6-1.4-.6l-1 1-1.1-.3c-.1-.1-.3.4-.7.6h-.4l-.6-.4-.7-.7-.5-.3-.4-1v-.5q0-1 2.2-1.7a4 4 0 0 1 2 0q.9-.8 3-.8c2.1 0 2.4.3 3 .7a6 6 0 0 1 2.9-.7q2.1 0 3 .8.8-.3 2 0 2.2.6 2.2 1.7v.5l-.4 1-.6.3-.6.7-.6.3s-.3.2-.4 0q-.6-.3-.7-.5c-.1-.2-.6.4-1 .2s-1-1-1-1-.9.8-1.4.7c-.6-.1-1-.6-1-.6s-.7.6-1.2.5-1.2-.9-1.2-.9"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M277.9 211.6s-.7.8-1.3.9c-.5 0-1.1-.5-1.1-.5s-.5.5-1 .6-1.4-.6-1.4-.6l-1 1-1.1-.3c-.1-.1-.3.4-.7.6h-.4l-.6-.4-.7-.7-.5-.3-.4-1v-.5q0-1 2.2-1.7a4 4 0 0 1 2 0q.9-.8 3-.8c2.1 0 2.4.3 3 .7a6 6 0 0 1 2.9-.7q2.1 0 3 .8.8-.3 2 0 2.2.6 2.2 1.7v.5l-.4 1-.6.3-.6.7-.6.3s-.3.2-.4 0q-.6-.3-.7-.5c-.1-.2-.6.4-1 .2s-1-1-1-1-.9.8-1.4.7c-.6-.1-1-.6-1-.6s-.7.6-1.2.5-1.2-.9-1.2-.9z"
                    />
                    <path
                        fill="#c8b100"
                        d="M276.5 207.6c0-1 .6-2 1.3-2s1.3 1 1.3 2-.5 1.8-1.3 1.8q-1.2-.1-1.3-1.9"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M276.5 207.6c0-1 .6-2 1.3-2s1.3 1 1.3 2-.5 1.8-1.3 1.8q-1.2-.1-1.3-1.9z"
                    />
                    <path
                        fill="#c8b100"
                        d="M277.3 207.6q0-1.7.5-1.8.6.2.7 1.8c.1 1.6-.3 1.7-.6 1.7q-.6-.2-.6-1.8"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M277.3 207.6q0-1.7.5-1.8.6.2.7 1.8c.1 1.6-.3 1.7-.6 1.7q-.6-.2-.6-1.8z"
                    />
                    <path
                        fill="#c8b100"
                        d="m271 215.3-.5-1a27 27 0 0 1 14.8 0l-.6.8-.3.8a23 23 0 0 0-6.6-.8c-2.6 0-5.2.3-6.6.8z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m271 215.3-.5-1a27 27 0 0 1 14.8 0l-.6.8-.3.8a23 23 0 0 0-6.6-.8c-2.6 0-5.2.3-6.6.8l-.2-.6"
                    />
                    <path
                        fill="#c8b100"
                        d="M277.8 217.7c2.4 0 5-.4 5.9-.6q1-.3 1-.8 0-.3-.4-.4a24 24 0 0 0-6.5-.8c-2.5 0-5 .3-6.4.8q-.3 0-.4.3 0 .6 1 .9c1 .2 3.5.6 5.8.6"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M277.8 217.7c2.4 0 5-.4 5.9-.6q1-.3 1-.8 0-.3-.4-.4a24 24 0 0 0-6.5-.8c-2.5 0-5 .3-6.4.8q-.3 0-.4.3 0 .6 1 .9c1 .2 3.5.6 5.8.6z"
                    />
                    <path
                        fill="#fff"
                        d="M283.5 208.4q0-.3.4-.4.5 0 .5.4t-.5.4a.4.4 0 0 1-.4-.4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".2"
                        d="M283.5 208.4q0-.3.4-.4.5 0 .5.4t-.5.4a.4.4 0 0 1-.4-.4zm-.2-1.4a.4.4 0 0 1 .4-.4q.4 0 .4.4t-.4.4a.4.4 0 0 1-.4-.4zm-1.1-1q0-.3.4-.3t.4.4-.4.4a.4.4 0 0 1-.4-.5zm-1.4-.4q0-.3.4-.4.5 0 .5.4t-.4.4q-.5-.1-.5-.4zm-1.4 0q0-.3.5-.3t.4.4q0 .3-.4.4a.4.4 0 0 1-.5-.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinecap="round"
                        strokeWidth=".3"
                        d="m287.8 211.2.2-1a2.7 2.7 0 0 0-2.7-2.8q-.8 0-1.3.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m283 209.2.2-.8q-.2-1.8-2.5-2-1 0-1.6.4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".2"
                        d="M288.2 210q0-.5.4-.5t.4.4-.4.4q-.5 0-.4-.4zm-.2-1.6q0-.3.4-.4a.4.4 0 0 1 .5.4q0 .3-.4.4-.5 0-.5-.4zm-1-1.1a.4.4 0 0 1 .5-.4q.3 0 .4.4a.4.4 0 0 1-.4.4.4.4 0 0 1-.5-.4zm-1.3-.7q0-.4.5-.4t.4.4q0 .5-.4.5a.4.4 0 0 1-.5-.5zm-1.4.1q0-.3.5-.4.6 0 .4.4t-.4.4q-.4-.1-.5-.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="m285.3 213.2-.5-.5s-.6.3-1.3.2c-.6 0-.9-1-.9-1s-.7.7-1.3.7-1-.6-1-.6-.7.5-1.3.4-1.2-.8-1.2-.8-.6.8-1.2.8c-.6.1-1-.5-1-.5s-.3.6-1.1.7-1.4-.6-1.4-.6-.4.7-1 1c-.5 0-1.2-.4-1.2-.4l-.1.5-.3.1.1.5a27 27 0 0 1 7.3-.9q4.3 0 7.3 1l.2-.6"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m285.3 213.2-.5-.5s-.6.3-1.3.2c-.6 0-.9-1-.9-1s-.7.7-1.3.7-1-.6-1-.6-.7.5-1.3.4-1.2-.8-1.2-.8-.6.8-1.2.8c-.6.1-1-.5-1-.5s-.3.6-1.1.7-1.4-.6-1.4-.6-.4.7-1 1c-.5 0-1.2-.4-1.2-.4l-.1.5-.3.1.1.5a27 27 0 0 1 7.3-.9q4.3 0 7.3 1l.2-.6z"
                    />
                    <path
                        fill="#fff"
                        d="M271.3 208.4q0-.3.4-.4.5 0 .4.4a.4.4 0 0 1-.4.4.4.4 0 0 1-.4-.4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".2"
                        d="M271.3 208.4q0-.3.4-.4.5 0 .4.4a.4.4 0 0 1-.4.4.4.4 0 0 1-.4-.4zm.2-1.4q0-.4.4-.4t.5.4q0 .5-.5.4a.4.4 0 0 1-.4-.4zm1-1q.1-.3.5-.3t.5.4q0 .3-.5.4a.4.4 0 0 1-.4-.5zm1.4-.4q0-.3.5-.4.6 0 .4.4t-.4.4q-.4-.1-.5-.4zm1.4 0q0-.3.5-.3.3 0 .4.4 0 .3-.4.4a.4.4 0 0 1-.5-.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinecap="round"
                        strokeWidth=".3"
                        d="m267.8 211.2-.2-1a2.7 2.7 0 0 1 2.7-2.8q.8 0 1.4.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m272.7 209.2-.3-.8c0-1 1.2-2 2.6-2a3 3 0 0 1 1.5.4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".2"
                        d="M266.6 210q0-.5.4-.5t.4.4a.4.4 0 0 1-.4.4q-.3 0-.4-.4zm.1-1.6q.1-.3.5-.4.5 0 .4.4t-.4.4q-.4-.1-.4-.4zm1-1.1q0-.4.5-.4a.4.4 0 0 1 .4.4.4.4 0 0 1-.4.4.4.4 0 0 1-.5-.4zm1.3-.7q0-.4.5-.4.3 0 .4.4 0 .5-.4.5a.4.4 0 0 1-.5-.5zm1.4.1q0-.3.5-.4a.4.4 0 0 1 .4.4.4.4 0 0 1-.4.4q-.5 0-.5-.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="M277.9 210.7h.2v.4q.1 1 1 1a1 1 0 0 0 1-.7l.2-.3v.4q.3.8 1.1.8 1 0 1-1v-.1l.4-.4.2.5-.1.4a1 1 0 0 0 1 1q.6 0 .9-.5l.2-.2v.3q0 .5.4.7l1-.4c1-.5.7-.7.7-.7v.4s-.5.8-1 1q-.3.4-.8.3-.5 0-.7-.6l-.7.2q-1 0-1.4-.8l-1.1.5q-.8 0-1.2-.6l-1 .4q-.9 0-1.4-.6-.4.6-1.2.6-.6 0-1-.4a2 2 0 0 1-1.3.6q-.6 0-1.1-.5-.4.8-1.4.8-.4 0-.7-.2-.2.5-.7.6t-.9-.2l-1-1 .1-.5.8.7q.8.5.9.4.4-.1.4-.7v-.3l.2.2q.3.5.9.5a1 1 0 0 0 1-1v-.9l.4.4v.1q.1 1 1 1t1.1-.9v-.3l.2.3q.4.6 1 .7 1 0 1.1-1v-.3h.2"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M277.9 210.7h.2v.4q.1 1 1 1a1 1 0 0 0 1-.7l.2-.3v.4q.3.8 1.1.8 1 0 1-1v-.1l.4-.4.2.5-.1.4a1 1 0 0 0 1 1q.6 0 .9-.5l.2-.2v.3q0 .5.4.7l1-.4c1-.5.7-.7.7-.7v.4s-.5.8-1 1q-.3.4-.8.3-.5 0-.7-.6l-.7.2q-1 0-1.4-.8l-1.1.5q-.8 0-1.2-.6l-1 .4q-.9 0-1.4-.6-.4.6-1.2.6-.6 0-1-.4a2 2 0 0 1-1.3.6q-.6 0-1.1-.5-.4.8-1.4.8-.4 0-.7-.2-.2.5-.7.6t-.9-.2l-1-1 .1-.5.8.7q.8.5.9.4.4-.1.4-.7v-.3l.2.2q.3.5.9.5a1 1 0 0 0 1-1v-.9l.4.4v.1q.1 1 1 1t1.1-.9v-.3l.2.3q.4.6 1 .7 1 0 1.1-1v-.3h.2z"
                    />
                    <path
                        fill="#c8b100"
                        d="M277.8 213.3q-4.5 0-7.3 1l-.3-.2.1-.3q3-1 7.5-1t7.6 1l.1.3-.3.2a27 27 0 0 0-7.4-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M277.8 213.3q-4.5 0-7.3 1l-.3-.2.1-.3q3-1 7.5-1t7.6 1l.1.3-.3.2a27 27 0 0 0-7.4-1z"
                    />
                    <path
                        fill="#fff"
                        d="M275 214.4q0-.4.5-.4a.4.4 0 0 1 .4.4.4.4 0 0 1-.4.4q-.5 0-.5-.4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M275 214.4q0-.4.5-.4a.4.4 0 0 1 .4.4.4.4 0 0 1-.4.4q-.5 0-.5-.4z"
                    />
                    <path
                        fill="#ad1519"
                        d="M277.9 214.5h-1q-.2 0-.3-.3l.3-.3h2l.2.3-.3.3h-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M277.9 214.5h-1q-.2 0-.3-.3l.3-.3h2l.2.3-.3.3h-1"
                    />
                    <path
                        fill="#058e6e"
                        d="M273.2 214.9h-.6a.3.3 0 0 1-.4-.2l.3-.3.6-.1.7-.1q.3 0 .4.2a.3.3 0 0 1-.3.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M273.2 214.9h-.6a.3.3 0 0 1-.4-.2l.3-.3.6-.1.7-.1q.3 0 .4.2a.3.3 0 0 1-.3.4h-.7"
                    />
                    <path
                        fill="#ad1519"
                        d="m270.5 215.3.3-.4h.7l-.4.6z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m270.5 215.3.3-.4h.7l-.4.6-.6-.2"
                    />
                    <path
                        fill="#fff"
                        d="M279.8 214.4q0-.4.4-.4t.5.4q0 .3-.5.4a.4.4 0 0 1-.4-.4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M279.8 214.4q0-.4.4-.4t.5.4q0 .3-.5.4a.4.4 0 0 1-.4-.4z"
                    />
                    <path
                        fill="#058e6e"
                        d="M282.5 214.9h.7l.3-.2-.2-.3-.7-.1-.7-.1q-.3 0-.4.2 0 .3.3.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M282.5 214.9h.7l.3-.2-.2-.3-.7-.1-.7-.1q-.3 0-.4.2 0 .3.3.4h.7"
                    />
                    <path
                        fill="#ad1519"
                        d="m285.1 215.4-.2-.5h-.7l.3.6z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m285.1 215.4-.2-.5h-.7l.3.6.6-.1"
                    />
                    <path
                        fill="#ad1519"
                        d="M277.8 217.1a25 25 0 0 1-6-.6 25 25 0 0 1 6-.7q3.6 0 6.1.7-2.5.6-6 .6"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".3"
                        d="M277.8 217.1a25 25 0 0 1-6-.6 25 25 0 0 1 6-.7q3.6 0 6.1.7-2.5.6-6 .6z"
                    />
                    <path
                        fill="#c8b100"
                        d="m285.2 212-.1-.3q-.3 0-.4.2l.1.4q.3 0 .4-.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m285.2 212-.1-.3q-.3 0-.4.2l.1.4q.3 0 .4-.3z"
                    />
                    <path
                        fill="#c8b100"
                        d="M280.6 211.2q0-.3-.3-.4l-.2.3q0 .3.2.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M280.6 211.2q0-.3-.3-.4l-.2.3q0 .3.2.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="M275.2 211.2q0-.3.2-.4l.3.3-.2.4q-.3 0-.3-.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M275.2 211.2q0-.3.2-.4l.3.3-.2.4q-.3 0-.3-.3z"
                    />
                    <path
                        fill="#c8b100"
                        d="m270.5 212 .1-.3q.3 0 .4.2l-.1.4q-.3 0-.4-.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m270.5 212 .1-.3q.3 0 .4.2l-.1.4q-.3 0-.4-.3z"
                    />
                    <path
                        fill="#c8b100"
                        d="m277.8 208.5-.8.5.6 1.3.2.1.3-.1.6-1.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m277.8 208.5-.8.5.6 1.3.2.1.3-.1.6-1.3-.9-.5"
                    />
                    <path
                        fill="#c8b100"
                        d="m276 210.5.4.5 1.3-.4.1-.2-.1-.2-1.3-.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m276 210.5.4.5 1.3-.4.1-.2-.1-.2-1.3-.3-.4.6"
                    />
                    <path
                        fill="#c8b100"
                        d="m279.6 210.5-.3.5-1.3-.4-.1-.2v-.2l1.4-.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m279.6 210.5-.3.5-1.3-.4-.1-.2v-.2l1.4-.3.4.6"
                    />
                    <path
                        fill="#c8b100"
                        d="m272.5 209-.7.7.9 1 .2.1.2-.1.2-1.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m272.5 209-.7.7.9 1 .2.1.2-.1.2-1.3-.8-.3"
                    />
                    <path
                        fill="#c8b100"
                        d="m271.1 211.2.5.5 1.2-.6v-.2l-.1-.2-1.3-.1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m271.1 211.2.5.5 1.2-.6v-.2l-.1-.2-1.3-.1-.3.6"
                    />
                    <path
                        fill="#c8b100"
                        d="m274.7 210.5-.3.6h-1.3l-.2-.2.1-.3 1.2-.6z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m274.7 210.5-.3.6h-1.3l-.2-.2.1-.3 1.2-.6.5.5"
                    />
                    <path
                        fill="#c8b100"
                        d="M269.8 211.4v.6l-1.4.2-.2-.1v-.2l1-.9z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M269.8 211.4v.6l-1.4.2-.2-.1v-.2l1-.9.6.4"
                    />
                    <path
                        fill="#c8b100"
                        d="M272.4 210.9q0-.5.5-.5l.5.5-.5.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M272.4 210.9q0-.5.5-.5l.5.5-.5.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="m283.2 209 .7.7-.9 1-.2.1-.1-.1-.3-1.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m283.2 209 .7.7-.9 1-.2.1-.1-.1-.3-1.3.8-.3"
                    />
                    <path
                        fill="#c8b100"
                        d="m284.6 211.2-.5.5-1.2-.6v-.2l.1-.2 1.3-.1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m284.6 211.2-.5.5-1.2-.6v-.2l.1-.2 1.3-.1.3.6"
                    />
                    <path
                        fill="#c8b100"
                        d="m281 210.5.3.6h1.3l.2-.2-.1-.3-1.2-.6z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m281 210.5.3.6h1.3l.2-.2-.1-.3-1.2-.6-.5.5"
                    />
                    <path
                        fill="#c8b100"
                        d="M285.7 211.4v.6l1.4.2.2-.1v-.2l-1-.9z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M285.7 211.4v.6l1.4.2.2-.1v-.2l-1-.9-.6.4"
                    />
                    <path
                        fill="#c8b100"
                        d="M277.4 210.4q0-.3.5-.4.4 0 .4.4t-.4.5z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M277.4 210.4q0-.3.5-.4.4 0 .4.4t-.4.5z"
                    />
                    <path
                        fill="#c8b100"
                        d="M282.3 210.9q.1-.5.5-.5t.5.5q0 .6-.5.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M282.3 210.9q.1-.5.5-.5t.5.5q0 .6-.5.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="M277 205.4q.1-.8.8-.8 1 0 1 .8c0 .8-.5.8-1 .8a1 1 0 0 1-.8-.8"
                    />
                    <path
                        fill="#c8b100"
                        d="M278.5 205.1v.6H277v-.6h.4v-1.3h-.5v-.5h.5v-.6h.6v.6h.6v.6h-.6v1.2h.4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M278.5 205.1v.6H277v-.6h.4v-1.3h-.5v-.5h.5v-.6h.6v.6h.6v.6h-.6v1.2h.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="M279 205.1v.6h-2.4v-.6h1v-1.3h-.7v-.5h.6v-.6h.6v.6h.6v.6h-.6v1.2h1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M278.1 204.6q.6.1.6.8t-.9.8a1 1 0 0 1-.8-.8q0-.6.6-.8"
                    />
                    <path
                        fill="#c8b100"
                        d="m268 212.2-.6-.7-.7-.3q0-.2.6-.3.3 0 .4.2v-.2s.3 0 .4.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m268 212.2-.6-.7-.7-.3q0-.2.6-.3.3 0 .4.2v-.2s.3 0 .4.3z"
                    />
                    <path
                        fill="#c8b100"
                        d="M268 212q.3-.3.5 0 .4.2.1.5l-.5-.1q-.2-.2 0-.5"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M268 212q.3-.3.5 0 .4.2.1.5l-.5-.1q-.2-.2 0-.5z"
                    />
                    <path
                        fill="#c8b100"
                        d="m287.5 212.2.6-.7c.2-.2.7-.3.7-.3q0-.2-.6-.3l-.4.2v-.2s-.3 0-.4.3v.7z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m287.5 212.2.6-.7c.2-.2.7-.3.7-.3q0-.2-.6-.3l-.4.2v-.2s-.3 0-.4.3v.7z"
                    />
                    <path
                        fill="#c8b100"
                        d="M287.5 212q-.2-.3-.5 0t-.1.5l.5-.1q.3-.2.1-.5"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M287.5 212q-.2-.3-.5 0t-.1.5l.5-.1q.3-.2.1-.5z"
                    />
                    <path
                        fill="#c8b100"
                        d="M267.2 223h21.4v-5.5h-21.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M267.2 223h21.4v-5.5h-21.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="M286.3 226.8h-16.9q1-.4 1-1.2 0-1-1-1.3h17-.1q-1 .3-1 1.3 0 .8 1 1.2"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".4"
                        d="M286.3 226.8h-16.9q1-.4 1-1.2 0-1-1-1.3h17-.1q-1 .3-1 1.3 0 .8 1 1.2z"
                    />
                    <path
                        fill="#c8b100"
                        d="M269.9 226.8h16q1 0 1 .7t-1 .8h-16q-1-.1-1-.8.1-1 1-.8"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M269.9 226.8h16q1 0 1 .7t-1 .8h-16q-1-.1-1-.8.1-1 1-.8z"
                    />
                    <path
                        fill="#c8b100"
                        d="M269.9 223h16q1 .1 1 .7t-1 .6h-16q-1 0-1-.6t1-.6"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M269.9 223h16q1 .1 1 .7t-1 .6h-16q-1 0-1-.6t1-.6z"
                    />
                    <path
                        fill="#005bbf"
                        d="M263 317.4q2.1 0 3.7-.8a8 8 0 0 1 3.7-.8q2.2 0 3.8.8c1.6.8 2.3.8 3.7.8q2.3 0 3.8-.8a8 8 0 0 1 3.6-.8 8 8 0 0 1 3.7.8q1.6.8 3.8.8v2.4a8 8 0 0 1-3.8-.9 8 8 0 0 0-3.7-.8q-2.2 0-3.6.8-1.5.8-3.8.9a8 8 0 0 1-3.7-.9 8 8 0 0 0-3.8-.8 8 8 0 0 0-3.7.8q-1.5.8-3.8.9v-2.4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M263 317.4q2.1 0 3.7-.8a8 8 0 0 1 3.7-.8q2.2 0 3.8.8c1.6.8 2.3.8 3.7.8q2.3 0 3.8-.8a8 8 0 0 1 3.6-.8 8 8 0 0 1 3.7.8q1.6.8 3.8.8v2.4a8 8 0 0 1-3.8-.9 8 8 0 0 0-3.7-.8q-2.2 0-3.6.8-1.5.8-3.8.9a8 8 0 0 1-3.7-.9 8 8 0 0 0-3.8-.8 8 8 0 0 0-3.7.8q-1.5.8-3.8.9v-2.4z"
                    />
                    <path
                        fill="#ccc"
                        d="M263 319.8q2.1 0 3.7-.9c1.6-.9 2.3-.8 3.7-.8q2.2 0 3.8.8c1.6.8 2.3.9 3.7.9a8 8 0 0 0 3.8-.9 8 8 0 0 1 3.6-.8q2.3 0 3.7.8 1.6.8 3.8.9v2.3a8 8 0 0 1-3.8-.9 8 8 0 0 0-3.7-.7q-2.2 0-3.6.7-1.5.8-3.8.9a7 7 0 0 1-3.7-.9q-1.5-.6-3.8-.7a8 8 0 0 0-3.7.7 8 8 0 0 1-3.8.9v-2.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M263 319.8q2.1 0 3.7-.9c1.6-.9 2.3-.8 3.7-.8q2.2 0 3.8.8c1.6.8 2.3.9 3.7.9a8 8 0 0 0 3.8-.9 8 8 0 0 1 3.6-.8q2.3 0 3.7.8 1.6.8 3.8.9v2.3a8 8 0 0 1-3.8-.9 8 8 0 0 0-3.7-.7q-2.2 0-3.6.7-1.5.8-3.8.9a7 7 0 0 1-3.7-.9q-1.5-.6-3.8-.7a8 8 0 0 0-3.7.7 8 8 0 0 1-3.8.9v-2.3"
                    />
                    <path
                        fill="#005bbf"
                        d="M263 322q2.1 0 3.7-.8 1.6-.6 3.7-.7 2.2 0 3.8.7c1.6.7 2.3.9 3.7.9a8 8 0 0 0 3.8-.9 8 8 0 0 1 3.6-.8 8 8 0 0 1 3.7.8q1.6.8 3.8.9v2.3a8 8 0 0 1-3.8-.9 8 8 0 0 0-3.7-.7q-2.2 0-3.6.7-1.5.9-3.8.9t-3.7-.8a8 8 0 0 0-3.8-.8 8 8 0 0 0-3.7.8q-1.5.8-3.8.8V322"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M263 322q2.1 0 3.7-.8 1.6-.6 3.7-.7 2.2 0 3.8.7c1.6.7 2.3.9 3.7.9a8 8 0 0 0 3.8-.9 8 8 0 0 1 3.6-.8 8 8 0 0 1 3.7.8q1.6.8 3.8.9v2.3a8 8 0 0 1-3.8-.9 8 8 0 0 0-3.7-.7q-2.2 0-3.6.7-1.5.9-3.8.9t-3.7-.8a8 8 0 0 0-3.8-.8 8 8 0 0 0-3.7.8q-1.5.8-3.8.8V322"
                    />
                    <path
                        fill="#ccc"
                        d="M263 326.7a8 8 0 0 0 3.7-.8q1.6-.8 3.7-.8 2.2 0 3.8.8c1.6.8 2.3.8 3.7.8q2.3 0 3.8-.9a8 8 0 0 1 3.6-.7q2.3 0 3.7.8a8 8 0 0 0 3.8.8v-2.3a8 8 0 0 1-3.8-.9 8 8 0 0 0-3.7-.7q-2.2 0-3.6.7-1.5.8-3.8.9t-3.7-.8a8 8 0 0 0-3.8-.8 8 8 0 0 0-3.7.8q-1.5.8-3.8.8v2.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M263 326.7a8 8 0 0 0 3.7-.8q1.6-.8 3.7-.8 2.2 0 3.8.8c1.6.8 2.3.8 3.7.8q2.3 0 3.8-.9a8 8 0 0 1 3.6-.7q2.3 0 3.7.8a8 8 0 0 0 3.8.8v-2.3a8 8 0 0 1-3.8-.9 8 8 0 0 0-3.7-.7q-2.2 0-3.6.7-1.5.8-3.8.9t-3.7-.8a8 8 0 0 0-3.8-.8 8 8 0 0 0-3.7.8q-1.5.8-3.8.8v2.3"
                    />
                    <path
                        fill="#005bbf"
                        d="M263 329a8 8 0 0 0 3.7-.8q1.6-.8 3.7-.8 2.2 0 3.8.8c1.6.8 2.3.8 3.7.8a8 8 0 0 0 3.8-.9 8 8 0 0 1 3.6-.7q2.3 0 3.7.8 1.6.8 3.8.8v-2.3a8 8 0 0 1-3.8-.8 8 8 0 0 0-3.7-.8 8 8 0 0 0-3.6.7 8 8 0 0 1-3.8.9q-2.2 0-3.7-.8t-3.8-.8q-2.1 0-3.7.8c-1.6.8-2.3.8-3.8.8v2.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M263 329a8 8 0 0 0 3.7-.8q1.6-.8 3.7-.8 2.2 0 3.8.8c1.6.8 2.3.8 3.7.8a8 8 0 0 0 3.8-.9 8 8 0 0 1 3.6-.7q2.3 0 3.7.8 1.6.8 3.8.8v-2.3a8 8 0 0 1-3.8-.8 8 8 0 0 0-3.7-.8 8 8 0 0 0-3.6.7 8 8 0 0 1-3.8.9q-2.2 0-3.7-.8t-3.8-.8q-2.1 0-3.7.8c-1.6.8-2.3.8-3.8.8v2.3z"
                    />
                    <path
                        fill="#c8b100"
                        d="m286.3 308-.1.5c0 1.5 1.2 2.6 2.7 2.6h-22c1.5 0 2.7-1.2 2.7-2.6l-.1-.5z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".4"
                        d="m286.3 308-.1.5c0 1.5 1.2 2.6 2.7 2.6h-22c1.5 0 2.7-1.2 2.7-2.6l-.1-.5z"
                    />
                    <path
                        fill="#c8b100"
                        d="M269.9 306.5h16q1 0 1 .8 0 .6-1 .7h-16q-1 0-1-.8.1-.6 1-.7"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M269.9 306.5h16q1 0 1 .8 0 .6-1 .7h-16q-1 0-1-.8.1-.6 1-.7z"
                    />
                    <path
                        fill="#c8b100"
                        d="M266.9 316.7h22V311h-22z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M266.9 316.7h22V311h-22z"
                    />
                    <path
                        fill="#ad1519"
                        d="M290.6 286.7c2.1 1.2 3.6 2.5 3.4 3.2q-.2.8-1.8 1.6c-1.6 1.1-2.5 3-1.8 4a5.5 5.5 0 0 1 .2-8.8"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M290.6 286.7c2.1 1.2 3.6 2.5 3.4 3.2q-.2.8-1.8 1.6c-1.6 1.1-2.5 3-1.8 4a5.5 5.5 0 0 1 .2-8.8z"
                    />
                    <path
                        fill="#ccc"
                        d="M270.1 305.6h15.6V229h-15.6v76.5z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M281.4 229.1v76.3m1.8-76.3v76.3m-13 .2h15.5V229h-15.6v76.5z"
                    />
                    <path
                        fill="#ad1519"
                        d="M254.2 257.7a50 50 0 0 1 23.3-2c9.3 1.6 16.4 5.3 15.9 8.4v.2l3.5-8.2c.6-3.3-7.3-7.5-17.6-9.2a54 54 0 0 0-9.2-.7c-6.7 0-12.4.8-15.9 2.1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".4"
                        d="M254.2 257.7a50 50 0 0 1 23.3-2c9.3 1.6 16.4 5.3 15.9 8.4v.2l3.5-8.2c.6-3.3-7.3-7.5-17.6-9.2a54 54 0 0 0-9.2-.7c-6.7 0-12.4.8-15.9 2.1v9.4"
                    />
                    <path
                        fill="#ad1519"
                        d="M285.7 267.3c4.4-.3 7.3-1.4 7.7-3.2q.2-2.2-3.8-4.5l-3.9.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M285.7 267.3c4.4-.3 7.3-1.4 7.7-3.2q.2-2.2-3.8-4.5l-3.9.3v7.4"
                    />
                    <path
                        fill="#ad1519"
                        d="M270 261.5a13 13 0 0 0-5.7 1.9v.2c-.5 1 1.8 3 5.8 5.4v-7.5"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M270 261.5a13 13 0 0 0-5.7 1.9v.2c-.5 1 1.8 3 5.8 5.4v-7.5"
                    />
                    <path
                        fill="#ad1519"
                        d="M295.4 282c.4-1.2-3.8-3.6-9.7-5.8-2.8-1-5-2-7.8-3.2-8.3-3.7-14.4-7.9-13.6-9.4v-.2c-.4.4-1 8-1 8-.8 1.3 4.8 5.5 12.4 9.1 2.4 1.2 7.6 3 10 4 4.3 1.4 8.7 4.3 8.3 5.3l1.4-7.7"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".4"
                        d="M295.4 282c.4-1.2-3.8-3.6-9.7-5.8-2.8-1-5-2-7.8-3.2-8.3-3.7-14.4-7.9-13.6-9.4v-.2c-.4.4-1 8-1 8-.8 1.3 4.8 5.5 12.4 9.1 2.4 1.2 7.6 3 10 4 4.3 1.4 8.7 4.3 8.3 5.3l1.4-7.7z"
                    />
                    <path
                        fill="#c8b100"
                        d="m263.9 254.4 2.1-6.6h-.5l-.5.1-1.4 4.8q-1.6-2-2.7-4.1l-1 .2h-1l4 5.7h.5zm6-6.6h-1.8v6.2h4.2v-.7h-2.6zm6.8 1 2 .3v-.7l-5.8-.5v.8h2l-.4 5.6h1.6l.5-5.4m2.4 6 .8.2.8.2.7-2.9.6 1.2.8 2.1 1 .2q.6 0 1 .3l-.3-.7-1.3-2.9q1.6 0 2.1-1.2.3-.8-.7-1.5-.8-.4-1.7-.5l-2.4-.5zm3-5.2c.7.2 1.5.3 1.5 1v.5q-.5 1.3-2 .9zm8 7-.2 2 .8.5.9.5.5-7-.7-.3-6.1 3.8.5.3.4.2 1.7-1.2 2.3 1.3zm-1.7-1.5 2-1.4-.2 2.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="M182.2 192.4c0-1 1-2 2-2s2.2 1 2.2 2c0 1.1-1 2-2.1 2a2 2 0 0 1-2.1-2z"
                    />
                    <path
                        fill="#ad1519"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M205.7 175.4c6.3 0 12 1 15.7 2.4a32 32 0 0 0 14.6 2.3c2.7 0 6.5.8 10.3 2.4a27 27 0 0 1 7.4 4.7l-1.5 1.4-.4 3.8-4.1 4.7-2 1.8-5 3.9-2.5.2-.7 2.1-31.6-3.7-31.7 3.7-.8-2.1-2.5-.2-4.9-4-2-1.7-4.1-4.7-.5-3.8-1.5-1.4a28 28 0 0 1 7.5-4.7 26 26 0 0 1 10.2-2.4q3 .3 6.6-.2a30 30 0 0 0 8-2q5.6-2.4 15.5-2.5z"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M206.2 217.1c-11.8 0-22.4-1.4-29.9-3.6a1 1 0 0 1-.8-1.2q0-.8.8-1.2a109 109 0 0 1 29.9-3.6c11.7 0 22.3 1.4 29.8 3.6a1.3 1.3 0 0 1 0 2.4c-7.5 2.2-18 3.6-29.8 3.6"
                    />
                    <path
                        fill="#ad1519"
                        d="M206.1 215.6c-10.6 0-20.2-1.2-27.5-3.1 7.3-2 16.9-3 27.5-3.1a115 115 0 0 1 27.6 3c-7.3 2-17 3.2-27.6 3.2"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="M206.9 215.7v-6.3m-1.7 6.3v-6.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".2"
                        d="M203.6 215.7v-6.3m-1.6 6.3v-6.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M200.6 215.7v-6.3m-2.8 5.9v-5.7m1.3 5.8v-6m-3.8 5.6v-5.2m1.3 5.4v-5.6"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M192 214.8V210m1 4.7V210m1.2 5v-5m-3.4 4.7v-4.5"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M189.7 214.5v-4.2m-1.2 4.1v-4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".6"
                        d="M186 214v-3m1.3 3.2v-3.5m-2.5 3.1V211"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".7"
                        d="M183.7 213.6v-2.3m-1.3 2v-1.8m-1.2 1.6v-1.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".9"
                        d="M179.8 212.8v-.7"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="M213.7 215.3v-5.8m-2.9 6v-6.1m-2.1 6.2v-6.3"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M206 207.4a108 108 0 0 0-30 3.9c.6-.3.5-1-.3-3-1-2.5-2.4-2.4-2.4-2.4 8.3-2.5 20-4 32.8-4a123 123 0 0 1 33 4s-1.5-.1-2.5 2.3q-1.2 2.8-.2 3c-7.5-2.2-18.4-3.7-30.3-3.7"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M206.1 201.9c-12.9 0-24.5 1.5-32.8 4a1 1 0 0 1-1.3-.6 1 1 0 0 1 .7-1.3 121 121 0 0 1 33.4-4.2c13.2 0 25.2 1.7 33.5 4.2q1 .4.7 1.3t-1.3.6c-8.4-2.5-20-4-32.9-4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".4"
                        d="M206.1 215.6c-10.6 0-20.2-1.2-27.5-3.1 7.3-2 16.9-3 27.5-3.1a115 115 0 0 1 27.6 3c-7.3 2-17 3.2-27.6 3.2z"
                    />
                    <path
                        fill="#fff"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M197 204.8q0-.9 1-1 .9.1 1 1 0 1.2-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="#ad1519"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M206.1 205.6H203a1 1 0 0 1 0-2h6.4q.9.1 1 1 0 1.2-1 1h-3.2"
                    />
                    <path
                        fill="#058e6e"
                        stroke="#000"
                        strokeWidth=".4"
                        d="m190.3 206.5-2.3.2q-.9 0-1.2-.8a1 1 0 0 1 1-1.1l2.2-.3 2.4-.3q.9 0 1.1.9 0 .9-.9 1l-2.3.4"
                    />
                    <path
                        fill="#fff"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M181 206.7q.1-1 1.1-1t1 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="#ad1519"
                        stroke="#000"
                        strokeWidth=".4"
                        d="m174 208.5 1.2-1.6 3.3.4-2.6 2-1.8-.8"
                    />
                    <path
                        fill="#058e6e"
                        stroke="#000"
                        strokeWidth=".4"
                        d="m222 206.5 2.3.2q.9 0 1.1-.8a1 1 0 0 0-.9-1.1l-2.2-.3-2.4-.3a1 1 0 0 0-1.1.9q0 .9.9 1l2.3.4"
                    />
                    <path
                        fill="#fff"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M213.3 204.8q0-.9 1-1c1-.1 1 .5 1 1s-.4 1-1 1a1 1 0 0 1-1-1m15.8 1.9q.1-1 1-1t1.1 1q0 .9-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="#ad1519"
                        stroke="#000"
                        strokeWidth=".4"
                        d="m238.2 208.5-1.1-1.6-3.3.4 2.6 2 1.8-.8"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M177.3 212.8c7.4-2.1 17.6-3.4 28.8-3.4 11.3 0 21.4 1.3 28.9 3.4"
                    />
                    <path
                        fill="#c8b100"
                        d="m182.3 183.8 1.4 1 2-3.2a7 7 0 0 1-3.6-7.2c.2-4.1 5.2-7.6 11.7-7.6q5 .1 8.5 2.4l.2-1.8a17 17 0 0 0-8.7-2.1c-7.4 0-13.2 4.1-13.5 9.1a9 9 0 0 0 3 7.6z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="m182.3 183.8 1.4 1 2-3.2a7 7 0 0 1-3.6-7.2c.2-4.1 5.2-7.6 11.7-7.6q5 .1 8.5 2.4l.2-1.8a17 17 0 0 0-8.7-2.1c-7.4 0-13.2 4.1-13.5 9.1a9 9 0 0 0 3 7.6l-1 1.8"
                    />
                    <path
                        fill="#c8b100"
                        d="M182.4 183.8a9 9 0 0 1-4-7.3q.2-5 5.3-8a9 9 0 0 0-3.4 6.8 9 9 0 0 0 3 6.7z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M182.4 183.8a9 9 0 0 1-4-7.3q.2-5 5.3-8a9 9 0 0 0-3.4 6.8 9 9 0 0 0 3 6.7l-.9 1.8"
                    />
                    <path
                        fill="#c8b100"
                        d="M160.1 187.1a9 9 0 0 1-2.3-5.9q0-2 1-3.8c2-4.2 8.4-7.2 16-7.2q3 0 5.9.6l-1 1.4a26 26 0 0 0-4.9-.4c-7 0-12.8 2.7-14.5 6.3a7 7 0 0 0-.7 3.1 7 7 0 0 0 2.7 5.6l-2.6 4.1-1.3-1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M160.1 187.1a9 9 0 0 1-2.3-5.9q0-2 1-3.8c2-4.2 8.4-7.2 16-7.2q3 0 5.9.6l-1 1.4a26 26 0 0 0-4.9-.4c-7 0-12.8 2.7-14.5 6.3a7 7 0 0 0-.7 3.1 7 7 0 0 0 2.7 5.6l-2.6 4.1-1.3-1z"
                    />
                    <path
                        fill="#c8b100"
                        d="M162.7 173.3a11 11 0 0 0-4 4.1 9 9 0 0 0-.9 3.8q.1 3.5 2.3 5.9l-1.5 2.5a10 10 0 0 1-2.3-6.5c0-4 2.5-7.5 6.4-9.8"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M162.7 173.3a11 11 0 0 0-4 4.1 9 9 0 0 0-.9 3.8q.1 3.5 2.3 5.9l-1.5 2.5a10 10 0 0 1-2.3-6.5c0-4 2.5-7.5 6.4-9.8z"
                    />
                    <path
                        fill="#c8b100"
                        d="M206 164.4c1.7 0 3.2 1.1 3.5 2.6q.4 2.1.4 4.5v1.1c.1 3.3.6 6.3 1.3 8.1l-5.2 5-5.2-5c.7-1.8 1.2-4.8 1.3-8.1v-1.1q0-2.4.4-4.5c.3-1.5 1.8-2.6 3.5-2.6"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M206 164.4c1.7 0 3.2 1.1 3.5 2.6q.4 2.1.4 4.5v1.1c.1 3.3.6 6.3 1.3 8.1l-5.2 5-5.2-5c.7-1.8 1.2-4.8 1.3-8.1v-1.1q0-2.4.4-4.5c.3-1.5 1.8-2.6 3.5-2.6z"
                    />
                    <path
                        fill="#c8b100"
                        d="M206 166q1.6.1 1.8 1.4.3 1.8.4 4.2v1q.2 5 1.2 7.7l-3.4 3.2-3.4-3.2q1-2.7 1.2-7.7v-1l.4-4.2a2 2 0 0 1 1.8-1.4"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M206 166q1.6.1 1.8 1.4.3 1.8.4 4.2v1q.2 5 1.2 7.7l-3.4 3.2-3.4-3.2q1-2.7 1.2-7.7v-1l.4-4.2a2 2 0 0 1 1.8-1.4z"
                    />
                    <path
                        fill="#c8b100"
                        d="m229.7 183.8-1.3 1-2-3.2a7 7 0 0 0 3.6-6.3v-.9c-.2-4.1-5.3-7.6-11.7-7.6a15 15 0 0 0-8.5 2.4l-.2-1.8a17 17 0 0 1 8.7-2.1c7.4 0 13.2 4.1 13.4 9.1a9 9 0 0 1-3 7.6z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="m229.7 183.8-1.3 1-2-3.2a7 7 0 0 0 3.6-6.3v-.9c-.2-4.1-5.3-7.6-11.7-7.6a15 15 0 0 0-8.5 2.4l-.2-1.8a17 17 0 0 1 8.7-2.1c7.4 0 13.2 4.1 13.4 9.1a9 9 0 0 1-3 7.6l1 1.8"
                    />
                    <path
                        fill="#c8b100"
                        d="M229.6 183.8a9 9 0 0 0 4.1-7.3c0-3.2-2.1-6.1-5.3-8a9 9 0 0 1 3.4 6.8 9 9 0 0 1-3.2 6.7z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M229.6 183.8a9 9 0 0 0 4.1-7.3c0-3.2-2.1-6.1-5.3-8a9 9 0 0 1 3.4 6.8 9 9 0 0 1-3.2 6.7l1 1.8"
                    />
                    <path
                        fill="#c8b100"
                        d="M252 187.1a9 9 0 0 0 2.2-5.9 9 9 0 0 0-.9-3.8c-2-4.2-8.4-7.2-16-7.2a29 29 0 0 0-6 .6l1 1.4a25 25 0 0 1 5-.4c7 0 12.8 2.7 14.4 6.3q.7 1.5.7 3.1a7 7 0 0 1-2.6 5.6l2.5 4.1 1.3-1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M252 187.1a9 9 0 0 0 2.2-5.9 9 9 0 0 0-.9-3.8c-2-4.2-8.4-7.2-16-7.2a29 29 0 0 0-6 .6l1 1.4a25 25 0 0 1 5-.4c7 0 12.8 2.7 14.4 6.3q.7 1.5.7 3.1a7 7 0 0 1-2.6 5.6l2.5 4.1 1.3-1z"
                    />
                    <path
                        fill="#c8b100"
                        d="M249.3 173.3a11 11 0 0 1 4 4.1 9 9 0 0 1 .9 3.8 9 9 0 0 1-2.3 5.9l1.6 2.5a10 10 0 0 0 2.3-6.5c0-4-2.6-7.5-6.5-9.8"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M249.3 173.3a11 11 0 0 1 4 4.1 9 9 0 0 1 .9 3.8 9 9 0 0 1-2.3 5.9l1.6 2.5a10 10 0 0 0 2.3-6.5c0-4-2.6-7.5-6.5-9.8z"
                    />
                    <path
                        fill="#fff"
                        d="M204.2 181.4q.1-1.7 1.8-1.8c1.7-.1 1.9.8 1.9 1.8s-.9 1.7-1.9 1.7a2 2 0 0 1-1.8-1.7"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M204.2 181.4q.1-1.7 1.8-1.8c1.7-.1 1.9.8 1.9 1.8s-.9 1.7-1.9 1.7a2 2 0 0 1-1.8-1.7z"
                    />
                    <path
                        fill="#fff"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M204.2 178q.1-1.7 1.8-1.8c1.7-.1 1.9.8 1.9 1.8s-.9 1.7-1.9 1.7a2 2 0 0 1-1.8-1.7m.4-3.7q.1-1.2 1.4-1.3 1.4.1 1.5 1.3-.1 1.3-1.5 1.4c-1.4.1-1.4-.6-1.4-1.4m.4-3.3q0-.9 1-1c1-.1 1 .5 1 1s-.4 1-1 1a1 1 0 0 1-1-1m.2-2.8q.1-.8.8-.8t.9.8q0 .7-.9.8a1 1 0 0 1-.8-.8"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".4"
                        d="m206.2 191.8 1.2.2a4.6 4.6 0 0 0 4.5 6 5 5 0 0 0 4.4-3c.1 0 .5-1.7.7-1.7s.1 1.8.2 1.7c.3 2.3 2.4 3.8 4.7 3.8a4.6 4.6 0 0 0 4.7-5l1.5-1.5.7 2a4 4 0 0 0-.4 1.9 4.4 4.4 0 0 0 4.5 4.2q2.5 0 3.8-1.9l.9-1.2v1.5c0 1.5.6 2.8 2 3 0 0 1.7.1 4-1.6 2.1-1.7 3.3-3.1 3.3-3.1l.2 1.7s-1.8 2.8-3.8 4c-1 .6-2.7 1.3-4 1q-2.1-.5-3-2.6a7 7 0 0 1-3.3 1 7 7 0 0 1-6.1-3.7 7 7 0 0 1-10.4-.3 7 7 0 0 1-4.6 1.8 7 7 0 0 1-5.7-3 7 7 0 0 1-5.7 3 7 7 0 0 1-4.7-1.8 7 7 0 0 1-10.4.3 7 7 0 0 1-6 3.7 7 7 0 0 1-3.4-1q-.8 2.1-3 2.7c-1.2.2-2.9-.5-4-1.1-2-1.2-3.8-4-3.8-4l.2-1.7s1.2 1.4 3.4 3.1 3.9 1.6 3.9 1.6c1.4-.2 2-1.5 2-3v-1.5l1 1.2a5 5 0 0 0 3.7 2c2.5 0 4.5-2 4.5-4.3a4 4 0 0 0-.4-2l.8-1.9 1.5 1.5v.6c0 2.4 2 4.4 4.6 4.4 2.4 0 4.4-1.5 4.7-3.8 0 0 0-1.6.2-1.7s.6 1.7.7 1.6a5 5 0 0 0 4.5 3.1 4.6 4.6 0 0 0 4.5-6l1.2-.2"
                    />
                    <path
                        fill="#fff"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M238.6 197.7q.4-1.4-.6-1.8-.9-.2-1.5 1.1-.4 1.4.6 1.8.9.2 1.5-1.1m-20.5-4c0-.8-.3-1.6-1-1.6q-.8 0-1.2 1.4 0 1.3.9 1.6 1-.1 1.3-1.4m-23.9 0c0-.8.4-1.6 1-1.6q1 0 1.2 1.4 0 1.3-.9 1.6-1-.1-1.2-1.4m-20.6 4q-.3-1.4.6-1.8 1-.2 1.5 1.1t-.5 1.8q-1 .2-1.6-1.1"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M182.7 184a5 5 0 0 1 2.2 2.9s0-.3.6-.6 1-.3 1-.3l-.1 1.3-.3 2.2-.7 1.6a2 2 0 0 0-1.5-.4 2 2 0 0 0-1.2.9s-.7-.6-1.2-1.3l-1.1-2-.7-1.1s.5-.2 1.1 0q.9.1.8.2a5 5 0 0 1 1-3.4m.4 9.8-.6-1q0-.7.3-1.2s-.9-.5-1.8-.7c-.7-.2-2-.2-2.3-.2h-1l.2.5.5.7a5 5 0 0 0-3 2 5 5 0 0 0 3.5 1l-.2.8v.6l1-.4c.3-.1 1.5-.5 2-1 .8-.4 1.5-1.1 1.5-1.1m2.7-.5.2-1.1-.6-1 1.4-1.3 2-.9 1.1-.4v.6l-.2.8a5 5 0 0 1 3.4 1 5 5 0 0 1-2.9 2l.7 1.2h-1c-.4 0-1.6 0-2.3-.2l-1.8-.7"
                    />
                    <path
                        fill="#ad1519"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M182.2 192.4c0-1 1-2 2-2s2.2 1 2.2 2c0 1.1-1 2-2.1 2a2 2 0 0 1-2.1-2"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M206.1 180.8a6 6 0 0 1 1.9 3.7s.2-.3.9-.5q1.1-.3 1.2-.2l-.5 1.4-.8 2.4-1 1.7a2 2 0 0 0-1.7-.7q-1 0-1.6.7-.2-.1-1-1.7l-.8-2.4-.5-1.4 1.2.2q1 .4.9.5.2-2.2 1.8-3.7"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M204.6 191.8a2 2 0 0 1-.5-1.2q0-.8.4-1.3s-.8-.7-1.8-1c-.7-.4-2-.7-2.5-.7l-1.2-.2.2.6.4.9a6 6 0 0 0-3.7 1.7q1.6 1.5 3.7 1.6l-.4 1-.2.6 1.2-.2c.4-.1 1.8-.4 2.5-.7 1-.4 1.9-1 1.9-1m3 0a2 2 0 0 0 .1-2.6s.9-.7 1.8-1a8 8 0 0 1 2.5-.7l1.2-.3-.1.7-.4.9q2.2.2 3.6 1.7a6 6 0 0 1-3.6 1.6l.5 1.6-1.2-.2-2.5-.7c-1-.4-1.8-1-1.8-1m22-8a5 5 0 0 0-2.2 3l-.7-.6-1-.3.2 1.3c0 .3 0 1.3.3 2.2q.4 1.6.6 1.6a2 2 0 0 1 1.5-.4q.9.2 1.3.9l1.1-1.3q1-1.4 1.1-2l.7-1.1s-.4-.2-1 0l-1 .2a5 5 0 0 0-1-3.4m-.3 9.8q.5-.4.6-1l-.2-1.2s.8-.5 1.7-.7c.7-.2 2-.2 2.3-.2h1.1l-.3.5-.4.7a5 5 0 0 1 2.9 2 5 5 0 0 1-3.5 1l.2.8v.6l-1-.4c-.3-.1-1.4-.5-2-1-.8-.4-1.4-1.1-1.4-1.1m-2.8-.5-.2-1.1q0-.7.6-1s-.6-.8-1.4-1.3c-.6-.4-1.7-.8-2-.9l-1-.4v.6l.2.8a5 5 0 0 0-3.5 1q1 1.4 3 2l-.5.7-.3.5h1c.4 0 1.7 0 2.3-.2l1.8-.7"
                    />
                    <path
                        fill="#ad1519"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M226 192.4c0-1 1-2 2-2s2.1 1 2.1 2a2 2 0 0 1-2 2 2 2 0 0 1-2.1-2m23.2 4.4c-.4-.5-1.4-.4-2.2.2q-1.2 1.2-.5 2.2 1 .8 2.3-.3c.7-.6 1-1.6.5-2"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".4"
                        d="m246.3 198 .7-1c.7-.6 1.8-.7 2.3-.2l.1.2s1-2 2.3-2.6 3.4-.5 3.4-.5a3 3 0 0 0-2.9-2.8 3 3 0 0 0-2.4 1l-.2-1s-1.3.3-1.9 1.8 0 3.6 0 3.6-.3-.9-.7-1.5a8 8 0 0 0-2.4-1.6l-1.3-.7-.1.5v.8a8 8 0 0 0-3.7.5 5 5 0 0 0 2.5 2.2l-.8.7-.4.5 1.3.2 2.5.2 1.7-.2m-80.3 0q0-.6-.7-1c-.7-.7-1.7-.8-2.2-.3l-.2.3s-1-2-2.3-2.7-3.3-.5-3.3-.5a3 3 0 0 1 2.8-2.8q1.6 0 2.4 1l.2-1s1.3.3 2 1.8c.5 1.5-.1 3.6-.1 3.6s.3-.9.8-1.5a8 8 0 0 1 2.4-1.6l1.3-.7v1.3a8 8 0 0 1 3.7.5 5 5 0 0 1-2.5 2.2l.8.7.4.5-1.2.2-2.6.2-1.7-.2"
                    />
                    <path
                        fill="#ad1519"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M163 196.8q1-.7 2.4.3 1.2 1 .4 2c-.5.6-1.5.5-2.2-.2s-1-1.6-.5-2m41-6.3q.2-1.8 2-2c1.8-.2 2.1.9 2.1 2 0 1-1 2-2 2a2 2 0 0 1-2.1-2"
                    />
                    <path
                        fill="#005bbf"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M201.8 160.6c0-2.2 1.9-4 4.3-4s4.2 1.8 4.2 4-1.9 4-4.3 4a4 4 0 0 1-4.2-4"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M205 149.3v2.2h-2.4v2.2h2.3v6.3H202l-.2.6q0 .9.3 1.6h7.9q.3-.7.3-1.6l-.2-.6h-2.8v-6.3h2.3v-2.2h-2.3v-2.2z"
                    />
                    <path
                        fill="#ccc"
                        d="M206.5 330.6a82 82 0 0 1-35.5-8.2 23 23 0 0 1-12.8-20.4v-32h96.4v32a23 23 0 0 1-12.8 20.4 81 81 0 0 1-35.3 8.2"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M206.5 330.6a82 82 0 0 1-35.5-8.2 23 23 0 0 1-12.8-20.4v-32h96.4v32a23 23 0 0 1-12.8 20.4 81 81 0 0 1-35.3 8.2z"
                    />
                    <path
                        fill="#ccc"
                        d="M206.3 270h48.3v-53.5h-48.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M206.3 270h48.3v-53.5h-48.3z"
                    />
                    <path
                        fill="#ad1519"
                        d="M206.3 302c0 12.6-10.7 22.9-24 22.9s-24.2-10.3-24.2-23v-32h48.2v32"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M168.6 320.9c1.5.8 3.6 2 5.8 2.6l-.1-54.7h-5.7z"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".5"
                        d="M158 301.6a24 24 0 0 0 5.5 15v-47.5h-5.4z"
                    />
                    <path
                        fill="#c7b500"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M179.4 324.7a27 27 0 0 0 5.6 0v-55.9h-5.6v56z"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M190 323.5a19 19 0 0 0 5.8-2.5v-52.2H190z"
                    />
                    <path
                        fill="#ad1519"
                        d="M158.1 270h48.2v-53.5H158z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M158.1 270h48.2v-53.5H158z"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M201 316c2.4-2 4.6-6.8 5.4-12.2l.1-35H201l.1 47.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M206.3 302c0 12.6-10.7 22.9-24 22.9s-24.2-10.3-24.2-23v-32h48.2v32"
                    />
                    <path
                        fill="#ad1519"
                        d="M254.6 270v32c0 12.6-10.8 22.9-24.1 22.9s-24.2-10.3-24.2-23v-32h48.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M254.6 270v32c0 12.6-10.8 22.9-24.1 22.9s-24.2-10.3-24.2-23v-32h48.3"
                    />
                    <path
                        fill="#c8b100"
                        d="m215.1 294.1.1.5q-.1 1-1.1 1a1 1 0 0 1-1.1-1v-.5h-1.5a2.5 2.5 0 0 0 1.8 2.9v3.9h1.6V297a3 3 0 0 0 1.7-1.6h4.4v-1.2zm21.8 0v1.2h-4l-.3.6 4.6 5.2-1.2 1-4.6-5.3-.2.1v8.7h-1.6V297h-.2l-4.8 5.2-1.2-1 4.7-5.3-.2-.4h-4V294h13zm2.6 0v1.2h4.4q.5 1.2 1.7 1.6v3.9h1.6V297a3 3 0 0 0 1.8-2.4v-.5h-1.6l.1.5q-.1 1-1 1-1.1 0-1.2-1l.1-.5zm-6.7 22.1a16 16 0 0 0 3.7-1l.8 1.4a18 18 0 0 1-4.3 1.2 3 3 0 0 1-2.6 2 3 3 0 0 1-2.5-2 18 18 0 0 1-4.6-1.2l.8-1.4q2 .8 4 1a3 3 0 0 1 1.5-1.3v-6.7h1.6v6.7q1.1.3 1.6 1.4zm-11-2.2-.8 1.4a17 17 0 0 1-3.6-3.1q-1.4.3-2.5-.5a2.4 2.4 0 0 1-.3-3.5l.1-.1a15 15 0 0 1-1.3-4.8h1.7a13 13 0 0 0 1 4q.8 0 1.4.2l4.1-4.5 1.3 1-4.1 4.5q.8 1.5-.1 2.8a15 15 0 0 0 3.1 2.6m-6-4.8q.6-.7 1.5 0c.9.7.5 1 .1 1.4a1 1 0 0 1-1.6.1 1 1 0 0 1 0-1.5m-2.2-4.5-1.6-.3-.3-4.3 1.7-.6v2.5q0 1.4.2 2.7m1.4-5.3 1.7.4v2.2c0-.8.3 2.1.3 2.1l-1.7.6-.3-2.7zm5.6 13.7a16 16 0 0 0 4.8 2.6l.4-1.6a14 14 0 0 1-4-2zm-.8 1.4a17 17 0 0 0 4.8 2.6l-1.2 1.1a19 19 0 0 1-4-2zm2.2-9.4 1.6.7 3-3.3-1-1.4zm-1.3-1-1-1.4 3-3.3 1.6.7zm18.1 9.9.8 1.4a17 17 0 0 0 3.6-3.1q1.4.3 2.5-.5a2.4 2.4 0 0 0 .3-3.5l-.1-.1a15 15 0 0 0 1.3-4.8h-1.7a13 13 0 0 1-1 4l-1.4.2-4.1-4.5-1.3 1 4.1 4.5a2.4 2.4 0 0 0 .1 2.8 15 15 0 0 1-3.1 2.6m6-4.8a1 1 0 0 0-1.5 0 1 1 0 0 0-.1 1.4 1 1 0 0 0 1.6.1 1 1 0 0 0 0-1.5m2.2-4.5 1.6-.3.3-4.3-1.7-.6v2.5q0 1.5-.2 2.8zm-1.4-5.3-1.7.4v2.2c0-.8-.3 2.1-.3 2.1l1.7.6.3-2.7zm-5.6 13.7a16 16 0 0 1-4.8 2.6l-.4-1.6a14 14 0 0 0 4-2zm.8 1.4a17 17 0 0 1-4.8 2.6l1.2 1.1a19 19 0 0 0 4-2zm-2.2-9.4-1.6.7-2.9-3.3 1-1.4zm1.3-1 1-1.4-3-3.3-1.6.7zm-20.1-8.7.5 1.6h4.5l.5-1.6zm21.1 0-.5 1.6h-4.5l-.5-1.6zm-11.6 21.9q.1-1 1.1-1a1 1 0 0 1 1.1 1q-.1 1-1 1a1 1 0 0 1-1.2-1m1.9-7.8 1.7-.4v-4.3l-1.7-.5zm-1.6 0-1.7-.4v-4.3l1.7-.5z"
                    />
                    <path
                        fill="#c8b100"
                        d="M211.5 294.2q.4-1.5 1.8-2V287h1.6v5.3q1.3.5 1.7 1.6h4.4v.3h-6a1 1 0 0 0-1-.6q-.6 0-1 .6zm12.2 0v-.3h4.1l.2-.3-5-5.7 1.2-1 5 5.6.2-.1V285h1.6v7.3h.3l4.9-5.5 1.2 1-4.9 5.5.3.6h4v.3zm21.6 0a1 1 0 0 1 1-.6q.7 0 1 .6h1.6q-.4-1.5-1.8-2V287h-1.6v5.3q-1.2.4-1.7 1.6h-4.4v.3zm-30.2-15 6 6.8 1.3-1-6.1-6.7.3-.6h4.4V276h-4.4a3 3 0 0 0-2.5-1.7 2.6 2.6 0 0 0-2.7 2.5 3 3 0 0 0 1.8 2.4v5.2h1.6v-5.2zm32 0v5.3h-1.7v-5.2l-.4-.2-6 6.8-1.3-1 6.2-6.9-.1-.3h-4.5V276h4.5a3 3 0 0 1 2.4-1.7 2.6 2.6 0 0 1 2.7 2.5 2.5 2.5 0 0 1-1.9 2.4zm-16.1 0v3.3h-1.7v-3.2a3 3 0 0 1-1.7-1.6h-4V276h4a3 3 0 0 1 2.5-1.7q2 .1 2.5 1.7h4v1.6h-4a3 3 0 0 1-1.6 1.6m-17.8 4-1.7.4v4.3l1.7.5zm1.6 0 1.7.4v4.3l-1.7.5zm30.6 0-1.7.4v4.3l1.7.5zm1.6 0 1.7.4v4.3l-1.7.5zm-25.5.8 1.6-.7 2.9 3.3-1 1.4zm-1.3 1-1 1.4 3 3.3 1.6-.7zm18.5-1.1-1.6-.7-3 3.3 1 1.4zm1.2 1 1 1.4-3 3.3-1.5-.7zm-20.3 9 .5-1.6h4.5l.5 1.6zm-6.7-17q0-1 1.2-1a1 1 0 0 1 1 1q0 1-1 1a1 1 0 0 1-1.2-1m12.1.8-.5 1.6H220l-.5-1.6zm0-1.6-.5-1.6H220l-.5 1.6zm15.7 17.8-.5-1.6h-4.5l-.5 1.6zm4.4-17q.1-1 1.1-1a1 1 0 0 1 1.1 1q-.1 1-1 1a1 1 0 0 1-1.2-1m-16.1 0q.1-1 1.1-1a1 1 0 0 1 1.1 1q-.1 1-1.1 1a1 1 0 0 1-1.1-1m6.2.8.5 1.6h4.6l.5-1.6zm0-1.6.5-1.6h4.6l.5 1.6zm-5.9 5-1.7.5v4.3l1.7.5V281m1.7 0 1.6.5v4.3l-1.6.5z"
                    />
                    <path
                        fill="none"
                        stroke="#c8b100"
                        strokeWidth=".3"
                        d="M232.7 316.3a16 16 0 0 0 3.7-1.1l.8 1.4a18 18 0 0 1-4.3 1.2 3 3 0 0 1-2.6 2 3 3 0 0 1-2.5-2 18 18 0 0 1-4.6-1.2l.8-1.4q2 .8 4 1a3 3 0 0 1 1.5-1.3v-6.7h1.6v6.7q1.1.3 1.6 1.4zm-4.7-20.4-.2-.5h-4V294h4l.2-.4-5-5.6 1.2-1 5 5.5h.2V285h1.7v7.3h.2l4.9-5.5 1.2 1-4.9 5.5.3.6h4v1.5h-4q0 .3-.3.5l4.7 5.3-1.3 1-4.6-5.3-.2.1v8.7h-1.6V297l-.2-.1-4.8 5.3-1.2-1 4.7-5.3m-12.8-16.7 6 6.8 1.3-1-6.1-6.7.3-.6h4.4V276h-4.4a3 3 0 0 0-2.5-1.7 2.6 2.6 0 0 0-2.6 2.5 3 3 0 0 0 1.7 2.4v5.2h1.6v-5.2zm6.5 34.8-.8 1.4a17 17 0 0 1-3.6-3.1q-1.4.3-2.5-.5a2.4 2.4 0 0 1-.3-3.5l.1-.1a15 15 0 0 1-1.2-4.8h1.6a13 13 0 0 0 1 4q.8 0 1.4.2l4.1-4.5 1.3 1-4.1 4.5q.8 1.5-.1 2.8a15 15 0 0 0 3.1 2.6zm-8.4-13.1V297a3 3 0 0 1-1.8-2.4q.2-1.6 1.8-2.4V287h1.6v5.3q1.3.4 1.7 1.6h4.4v1.5h-4.4a3 3 0 0 1-1.6 1.6v3.9h-1.7m2.3 8.3q.7-.7 1.6 0c.9.7.5 1 .1 1.4a1 1 0 0 1-1.6.1 1 1 0 0 1 0-1.5zm-2-4.5-1.7-.3-.3-4.3 1.7-.6v2.5q0 1.4.3 2.7zm1.4-5.3 1.6.4v2.2c0-.8.3 2.1.3 2.1l-1.7.6-.3-2.7v-2.6zm5.5 13.7a16 16 0 0 0 4.8 2.6l.4-1.6a14 14 0 0 1-4-2l-1.2 1m-.8 1.4a17 17 0 0 0 4.8 2.6l-1.2 1.1a19 19 0 0 1-4-2l.4-1.7"
                    />
                    <path
                        fill="none"
                        stroke="#c8b100"
                        strokeWidth=".3"
                        d="m221.9 305.1 1.6.7 3-3.3-1-1.4-3.6 4m-1.3-1-1-1.4 3-3.3 1.6.7-3.6 4m-7.6-9.5q.1-1 1-1 1.1.1 1.2 1-.1 1-1.1 1.1a1 1 0 0 1-1.1-1zm25.7 19.4.8 1.4a17 17 0 0 0 3.6-3.1q1.4.3 2.6-.5a2.4 2.4 0 0 0 .2-3.5l-.1-.1a15 15 0 0 0 1.3-4.8h-1.7a13 13 0 0 1-1 4l-1.4.2-4.1-4.5-1.3 1 4.1 4.5a2.4 2.4 0 0 0 .1 2.8 15 15 0 0 1-3 2.6zm8.4-13.1V297a3 3 0 0 0 1.8-2.4q0-1.6-1.8-2.4V287h-1.6v5.3q-1.2.4-1.7 1.6h-4.4v1.5h4.4q.5 1.2 1.7 1.6v3.9zm-2.3 8.3a1 1 0 0 0-1.6 0 1 1 0 0 0-.1 1.4 1 1 0 0 0 1.6.1 1 1 0 0 0 0-1.5zm2-4.5 1.7-.3.3-4.3-1.7-.6v2.5q0 1.4-.2 2.7zm-1.3-5.3-1.7.4v2.2c0-.8-.3 2.1-.3 2.1l1.7.6.3-2.7v-2.6m1.6-20.1v5.2h-1.6v-5.2l-.4-.2-6 6.8-1.2-1 6-7v-.2h-4.5V276h4.4a3 3 0 0 1 2.5-1.7 2.6 2.6 0 0 1 2.6 2.5 3 3 0 0 1-1.8 2.4zm-16 0v3.2h-1.7v-3.2a3 3 0 0 1-1.7-1.6h-4V276h4q.7-1.6 2.5-1.7c1.8-.1 2.2.7 2.5 1.7h4v1.6h-4a3 3 0 0 1-1.6 1.6zm8.8 33.8a16 16 0 0 1-4.8 2.6l-.4-1.6a14 14 0 0 0 4-2l1.2 1m.8 1.4a17 17 0 0 1-4.8 2.6l1.2 1.1a19 19 0 0 0 4-2l-.4-1.7m-27.4-31.4-1.7.5v4.3l1.7.5v-5.2m1.7 0 1.6.4v4.3l-1.6.5V283m30.5 0-1.7.5v4.3l1.7.5V283"
                    />
                    <path
                        fill="none"
                        stroke="#c8b100"
                        strokeWidth=".3"
                        d="m247.1 283.1 1.7.5v4.3l-1.7.5V283m-8.6 22-1.6.7-2.9-3.3 1-1.4 3.5 4m1.3-1 1-1.4-3-3.3-1.6.7 3.6 4m-18.2-20 1.6-.7 3 3.3-1 1.4-3.6-4m-1.3 1-1 1.4 3 3.3 1.6-.7-3.6-4m18.5-1.1-1.6-.7-3 3.3 1 1.4 3.6-4m1.2 1 1 1.4-3 3.2-1.5-.6 3.5-4m-20.3 9 .5-1.6h4.5l.5 1.6h-5.5m0 1.5.5 1.6h4.5l.5-1.6h-5.5M213 277q0-1 1.2-1 1 0 1 1c0 1-.4 1-1 1a1 1 0 0 1-1.2-1zm12.1.8-.5 1.6h-4.5l-.5-1.6h5.5m0-1.6-.5-1.6h-4.5l-.5 1.6h5.5m20.1 18.5q.1-.9 1.1-1 1 .1 1.1 1-.1 1-1 1.1a1 1 0 0 1-1.2-1zm-4.4-.7-.5-1.6h-4.5l-.5 1.6h5.5m0 1.5-.5 1.6h-4.5l-.5-1.6h5.5m-11.6 21.9q.1-1 1.1-1t1.1 1c.1 1-.5 1-1 1a1 1 0 0 1-1.2-1zm1.9-7.8 1.7-.4v-4.3l-1.7-.5v5.2m-1.6 0-1.7-.4v-4.3l1.7-.5v5.2m15.7-32.6q.1-1 1.1-1a1 1 0 0 1 1.1 1q-.1 1-1 1a1 1 0 0 1-1.2-1zm-16.1 0q.1-1 1.1-1a1 1 0 0 1 1.1 1q-.1 1-1 1a1 1 0 0 1-1.2-1zm6.2.8.5 1.6h4.6l.5-1.6h-5.5m0-1.6.4-1.6h4.6l.5 1.6h-5.5m-6 5-1.6.5v4.3l1.6.5V281m1.7 0 1.6.5v4.3l-1.6.5V281"
                    />
                    <path
                        fill="#058e6e"
                        d="M227.7 294.7a2.6 2.6 0 0 1 2.6-2.5 2.6 2.6 0 0 1 2.6 2.5 2.6 2.6 0 0 1-2.6 2.4c-1.4 0-2.6-1-2.6-2.4"
                    />
                    <path
                        fill="#db4446"
                        d="M230.9 229.7v-.6l.1-.3-2.3-.1a6 6 0 0 1-2.3-1.2q-1-1-1.6-1.2c-1.3-.2-2.3.4-2.3.4s1 .4 1.7 1.3 1.5 1.3 1.8 1.4c.6.2 2.6 0 3.1.1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M230.9 229.7v-.6l.1-.3-2.3-.1a6 6 0 0 1-2.3-1.2q-1-1-1.6-1.2c-1.3-.2-2.3.4-2.3.4s1 .4 1.7 1.3 1.5 1.3 1.8 1.4c.6.2 2.6 0 3.1.1z"
                    />
                    <path
                        fill="#ed72aa"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M238.1 227.5v1.4c.2.6-.1 1.2 0 1.5q0 .5.3.9l.2.9-.7-.5-.6-.4v1l.6 1.1 1 1.3c.2.5.1 1.4.1 1.4s-.4-.7-.8-.8l-1.2-.7s.7.8.7 1.5l-.3 1.6c-.1.2-.3-.7-.8-1.1l-1-.9s.4 1.2.4 2v2.3l-.9-1-1-.7c0-.2.5.6.6 1.1s.3 2.3 1.8 4.5c1 1.3 2.3 3.6 5.3 2.9s1.9-4.8 1.3-6.7a17 17 0 0 1-1-4.6c0-.8.6-2.9.5-3.3a8 8 0 0 1 .2-3.1l.9-2.3.4-1.3.1-1.3.7 1.3.1 1.5s.1-1 1-1.6 1.8-1.1 2-1.4l.3-.5c-.1 0 0 1.8-.6 2.6l-1.7 2s.7-.3 1.2-.3h.9s-.6.4-1.4 1.6c-.8 1-.5 1.2-1 2.1s-1 1-1.7 1.5c-1 .8-.5 4.2-.4 4.7.2.5 2 4.5 2 5.5s.2 3.2-1.5 4.6c-1.1 1-3 1-3.4 1.2-.4.3-1.2 1.1-1.2 2.8s.6 2 1 2.4c.6.5 1.2.2 1.3.6l.5.7q.3.3.2.8c0 .3-.8 1.1-1.1 1.7l-.8 2.4c0 .2-.1 1 .1 1.3 0 0 .9 1 .3 1.2-.4.2-.8-.2-1-.2l-.9.5q-.3-.1-.4-.8l-.1-.7q-.3 0-.4.5c0 .2 0 .8-.3.8l-.8-.5c-.2 0-.8-.2-.8-.4q.1-.6.7-1 .8-.1.5-.5t-.7 0c-.3.4-.8 0-.7-.2v-.8c0-.2-.4-.5.1-.8s.8.2 1.4.1q.8 0 1-.6.3-.6-.2-1.4l-.9-.8-.3-.9v2.2l-.7-.8c-.3-.3-.6-1.3-.6-1.3v1.3l.2.8c-.1.1-.8-.7-1-.8l-1-1-.4-1.4v-1.5l.4-1h-1.4c-.7 0-1.2-.3-1.5.2q-.4.8.2 2.8.5 1.8.4 2.1l-.7.8h-.9l-1.2-.3h-1.3l-1.1-.3c-.3.1-.8.3-.6.7q.1.8-.5.7l-.9-.2q-.8 0-.8-.4t.4-.7q.3-.5 0-.5h-.6q-.3.4-.8.4-.3-.1-.4-1c-.1-.9-.7-1.2 0-1.1.5 0 1.3.4 1.4 0q.2-.4-.2-.7c-.4-.3-.8-.4-.3-.7l.7-.5c.1-.2.4-.8.7-.6.6.2 0 .7.6 1.3s1 1 2 .8q1.4 0 1.3-.5l-.1-1v-1s-.4.3-.5.6l-.4.8v-2l-.2-.8-.3.9-.1 1s-.7-.5-.5-1.5l.1-2c.2-.3.7-1.5 2-1.6h2.6l2-.3s-2.8-1.4-3.5-1.9a10 10 0 0 1-2-2l-.6-1.6s-.5 0-1 .3l-1.2 1-.7 1 .1-1.2v-.8s-.4 1.2-1 1.7l-1.4 1v-.8l.2-1s-.4.8-1.1 1c-.7 0-1.8 0-1.9.4q.2.8 0 1.4c0 .3-.4.5-.4.5l-.8-.4-.7.2c-.1.1-.3-.4-.2-.7s.7-.6.5-.8l-.8.2c-.3.1-.8.3-.8-.2q.2-.6 0-1 0-.4.2-.6l1.2-.1q0-.4-.8-.6-1-.2-.5-.8.3-.2.5-.6c.1-.2.2-.7.7-.5.5.3.4.8 1 1a4 4 0 0 0 2-.2l1.5-1 1.5-1-1-.8q-.5-.7-1-1l-1.8-.6-1.7-.5.8-.3q.4-.5.8-.6h.3-1.4c-.3-.1-1-.6-1.3-.6l-.8.1s.8-.4 1.4-.5l1-.1s-.9-.3-1.1-.6l-.6-1q-.2-.3-.6-.5l-1 .3q-.6 0-.6-.6l-.1-.5c-.2-.3-.6-.8-.2-1h1.4q-.2-.4-.8-.8c-.4-.2-1-.5-.7-.8l.8-.5c.2-.3.3-1 .7-.7.4.2.8 1.2 1.1 1.1s.3-.8.3-1c0-.4 0-1 .2-.9l1 .5q.8-.1 1 .2 0 .5-.6 1-.4.6-.3 1.4.4 1 1.2 1.4t1.7.9c.5.3 1.7 1.2 2.1 1.3l.8.4s.5-.2 1.1-.2 2.1 0 2.6-.2 1.3-.6 1-1c-.1-.6-1.3-1-1.2-1.4q0-.5 1.2-.4c.8 0 1.8.1 2-1q.5-1.4-.8-1.8c-1-.2-1.8-.2-2-1q-.4-1-.2-1.1.3-.3 1.4-.4 1.3 0 1.9-.2l.6-.9c.3-.2 1.4-.4 1.4-.4s1.4.7 2.7 1.7l2.2 2.1"
                    />
                    <path d="m228.1 226.8-.2-.6v-.3s.8 0 .7.3q0 .2-.3.3z" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m228.1 226.8-.2-.6v-.3s.8 0 .7.3q0 .2-.3.3z"
                    />
                    <path d="M232 225.4v-.4s.7 0 1 .3c.5.4.9 1 .9 1l-.8-.4h-.5l-.3-.1v-.3z" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="M232 225.4v-.4s.7 0 1 .3c.5.4.9 1 .9 1l-.8-.4h-.5l-.3-.1v-.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m237.3 231.3-.4-.7-.3-.4"
                    />
                    <path
                        fill="#db4446"
                        d="M217.4 226.6s.5.4.8.4h.8s.2-.5.1-.8c-.2-1.2-1.2-1.4-1.2-1.4s.3.7.1 1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M217.4 226.6s.5.4.8.4h.8s.2-.5.1-.8c-.2-1.2-1.2-1.4-1.2-1.4s.3.7.1 1z"
                    />
                    <path
                        fill="#db4446"
                        d="M215.2 227.6s-.4-.7-1.3-.6-1.4.8-1.4.8h1.2c.3.3.4 1 .4 1l.7-.6z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M215.2 227.6s-.4-.7-1.3-.6-1.4.8-1.4.8h1.2c.3.3.4 1 .4 1l.7-.6z"
                    />
                    <path
                        fill="#db4446"
                        d="M214.2 230.6s-.8.1-1.2.6-.3 1.3-.3 1.3.4-.5.9-.5l1 .2-.1-.8z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M214.2 230.6s-.8.1-1.2.6-.3 1.3-.3 1.3.4-.5.9-.5l1 .2-.1-.8z"
                    />
                    <path d="m228.2 230.5.3-.5.3.5z" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m228.2 230.5.3-.5.3.5h-.7"
                    />
                    <path d="m229 230.5.3-.5.4.5z" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m229 230.5.3-.5.4.5h-.8"
                    />
                    <path d="m228.6 227.3.8.3-.7.4-.1-.6" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m228.6 227.3.8.3-.7.4-.1-.6"
                    />
                    <path d="m229.5 227.6.7.2-.5.4z" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m229.5 227.6.7.2-.5.4-.2-.6"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M224.2 233.7s-.7.2-1 .6q-.4.9-.3 1c.1.1.6-.5 1.5-.3l1.2.3 1.3-.3s-.7.8-.7 1.3l.2 1.1c0 .7-.6 1.6-.6 1.6l1-.3 1.7-.8.9-1s-.2 1 0 1.4l.2 1.6.8-.6q.5-.2.9-.7l.3-1s0 .8.4 1.3l.6 1.6s.3-.8.6-1.1l.7-1-.1-.9.4.8m-11 .6s.5-.8 1-1l1.1-.8.9-.4m1 5 1.3-.8 1-1"
                    />
                    <path
                        fill="#db4446"
                        d="M216.6 240.4s-.4-.5-1.1-.3c-.7 0-1.2.9-1.2.9s.6-.2 1-.1.6.4.6.4l.4-.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M216.6 240.4s-.4-.5-1.1-.3c-.7 0-1.2.9-1.2.9s.6-.2 1-.1.6.4.6.4l.4-.4z"
                    />
                    <path
                        fill="#db4446"
                        d="M215.8 243.2s-.6 0-1.1.3c-.5.4-.5 1.2-.5 1.2s.4-.4.8-.3l.9.2v-.6c.2-.4-.1-.8-.1-.8"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M215.8 243.2s-.6 0-1.1.3c-.5.4-.5 1.2-.5 1.2s.4-.4.8-.3l.9.2v-.6c.2-.4-.1-.8-.1-.8z"
                    />
                    <path
                        fill="#db4446"
                        d="M217.2 245.8s0 .8.3 1.3c.4.5 1.1.5 1.1.5l-.3-.7.3-.8q.1-.1-.7-.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M217.2 245.8s0 .8.3 1.3c.4.5 1.1.5 1.1.5l-.3-.7.3-.8q.1-.1-.7-.3zm16 1.3s2 1.2 1.9 2.2c0 1-1 2.3-1 2.3"
                    />
                    <path
                        fill="#db4446"
                        d="M224.2 252.6s-.4-.6-1.1-.6-1.4.7-1.4.7.8-.1 1 .2l.5.6.5-.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M224.2 252.6s-.4-.6-1.1-.6-1.4.7-1.4.7.8-.1 1 .2l.5.6.5-.3z"
                    />
                    <path
                        fill="#db4446"
                        d="M222.2 255.3s-1-.1-1.4.3-.4 1.3-.4 1.3.6-.6 1-.5q.9.1 1 .3v-.7l-.3-.7"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M222.2 255.3s-1-.1-1.4.3-.4 1.3-.4 1.3.6-.6 1-.5q.9.1 1 .3v-.7l-.3-.7z"
                    />
                    <path
                        fill="#db4446"
                        d="M224 258.1s-.3.7 0 1.1 1 .8 1 .8-.3-.4-.2-.8c.1-.3.7-.8.7-.8l-1.4-.2"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M224 258.1s-.3.7 0 1.1 1 .8 1 .8-.3-.4-.2-.8c.1-.3.7-.8.7-.8l-1.4-.2z"
                    />
                    <path
                        fill="#db4446"
                        d="M236 259.3s-.8-.2-1.2 0c-.5.3-.8 1.4-.8 1.4s.7-.6 1.2-.5q.9.1 1 .3v-.8z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M236 259.3s-.8-.2-1.2 0c-.5.3-.8 1.4-.8 1.4s.7-.6 1.2-.5q.9.1 1 .3v-.8z"
                    />
                    <path
                        fill="#db4446"
                        d="M236.4 262.2s-.6.6-.4 1.1l.6 1s0-.7.2-1l1-.3-.7-.5z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M236.4 262.2s-.6.6-.4 1.1l.6 1s0-.7.2-1l1-.3-.7-.5z"
                    />
                    <path
                        fill="#db4446"
                        d="M239.4 263s-.3.8.2 1.3l1 .5q-.2-.3-.2-1.1.3-.7.5-.7l-.8-.2-.7.3"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M239.4 263s-.3.8.2 1.3l1 .5q-.2-.3-.2-1.1.3-.7.5-.7l-.8-.2-.7.3z"
                    />
                    <path
                        fill="#ffd691"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M208.8 316.4q3 1 3 3.8c0 2.3-2.2 4-5 4-3 0-5.3-1.7-5.3-4 0-1.7 1-3.6 3-3.8l-.2-.4-.7-.7h1.2l.8.5.5-.7.6-.5.6.6.3.5.7-.4.8-.3s0 .4-.2.7l-.1.7"
                    />
                    <path
                        fill="#058e6e"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M206.3 326.7s-3.8-2.6-5.5-3c-2-.4-4.5 0-5.5 0 0 0 1.2.8 1.8 1.4.5.5 2.3 1.5 3.3 1.8 3 .8 6-.2 6-.2m1 .2s2.4-2.5 5-2.9c3-.4 5 .3 6.2.6l-1.5.8c-.5.3-2 1.5-4 1.6-2 0-4.4-.3-4.8-.2l-.9.1"
                    />
                    <path
                        fill="#ad1519"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M206.7 323.8a5 5 0 0 1 0-7.1 5 5 0 0 1 1.5 3.5 5 5 0 0 1-1.5 3.6"
                    />
                    <path
                        fill="#058e6e"
                        stroke="#000"
                        strokeWidth=".5"
                        d="M205.7 329s.6-1.5.6-2.7l-.1-2.1h.8s.3 1.1.3 2l-.1 2.4-.7.1-.8.3"
                    />
                    <path
                        fill="#fff"
                        d="M254 190.7q.1-.9 1-1 1 .1 1.1 1-.1 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M254 190.7q.1-.9 1-1 1 .1 1.1 1-.1 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M255.4 188.2q.1-1 1.1-1t1 1c0 1-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M255.4 188.2q.1-1 1.1-1t1 1c0 1-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M256.4 185.2q.1-.9 1-1 1 .1 1.1 1c.1.9-.5 1-1 1a1 1 0 0 1-1.1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M256.4 185.2q.1-.9 1-1 1 .1 1.1 1c.1.9-.5 1-1 1a1 1 0 0 1-1.1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M256.5 182q.1-.9 1-1 1 .1 1.1 1-.1 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M256.5 182q.1-.9 1-1 1 .1 1.1 1-.1 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M255.7 179q.1-1 1-1 1.1 0 1.2 1c.1 1-.5 1-1.1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M255.7 179q.1-1 1-1 1.1 0 1.2 1c.1 1-.5 1-1.1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M254.1 176.1q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M254.1 176.1q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M252 173.8q0-1 1-1c1 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M252 173.8q0-1 1-1c1 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M249.4 171.8q.1-.9 1.1-1a1 1 0 0 1 0 2q-1 0-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M249.4 171.8q.1-.9 1.1-1a1 1 0 0 1 0 2q-1 0-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M246.5 170.3q0-1 1-1c1 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M246.5 170.3q0-1 1-1c1 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M243.3 169.1q.1-.9 1.1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M243.3 169.1q.1-.9 1.1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M239.9 168.5q0-.9 1-1c1-.1 1 .5 1 1s-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M239.9 168.5q0-.9 1-1c1-.1 1 .5 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M236.6 168.3q0-.9 1-1c1-.1 1 .5 1 1s-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M236.6 168.3q0-.9 1-1c1-.1 1 .5 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M233.3 168.5q.1-1 1-1t1.1 1c.1 1-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M233.3 168.5q.1-1 1-1t1.1 1c.1 1-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M230.1 168.5q.1-1 1-1t1.1 1c.1 1-.5 1-1 1a1 1 0 0 1-1.1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M230.1 168.5q.1-1 1-1t1.1 1c.1 1-.5 1-1 1a1 1 0 0 1-1.1-1z"
                    />
                    <path
                        fill="#fff"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M231.7 171.2q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1m.6 3.1q0-1 1-1c1 0 1 .4 1 1q0 .9-1 1a1 1 0 0 1-1-1m0 3q.2-.9 1.1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1m-1 2.8q.1-.9 1-1 1 .1 1.1 1 0 1-1 1a1 1 0 0 1-1-1m-1.9 2.6q.1-.9 1-1 1.1.1 1.2 1 0 1.2-1.1 1-1 0-1-1"
                    />
                    <path
                        fill="#fff"
                        d="M227.6 166.5q.1-.9 1.1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M227.6 166.5q.1-.9 1.1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M224.8 165q0-1 1-1c1 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M224.8 165q0-1 1-1c1 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M221.6 164q.1-1 1-1t1.1 1q-.1.9-1 1-1-.1-1.1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M221.6 164q.1-1 1-1t1.1 1q-.1.9-1 1-1-.1-1.1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M218.3 163.4q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1.1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M218.3 163.4q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1.1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M215 163.5q.1-1 1.1-1t1 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M215 163.5q.1-1 1.1-1t1 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M211.7 164q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M211.7 164q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M208.6 165.1q.1-.9 1-1 1 .1 1.1 1c.1.9-.5 1-1 1a1 1 0 0 1-1.1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M208.6 165.1q.1-.9 1-1 1 .1 1.1 1c.1.9-.5 1-1 1a1 1 0 0 1-1.1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M156 190.7q0-.9 1-1c1-.1 1 .5 1 1q0 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M156 190.7q0-.9 1-1c1-.1 1 .5 1 1q0 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M154.5 188.2q.1-1 1-1t1 1c0 1-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M154.5 188.2q.1-1 1-1t1 1c0 1-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M153.5 185.2q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M153.5 185.2q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M153.4 182q.1-.9 1-1 1 .1 1.1 1-.1 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M153.4 182q.1-.9 1-1 1 .1 1.1 1-.1 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M154.2 179q.1-1 1-1t1 1c0 1-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M154.2 179q.1-1 1-1t1 1c0 1-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M155.8 176.1q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M155.8 176.1q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M158 173.8q0-1 1-1c1 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M158 173.8q0-1 1-1c1 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M160.5 171.8q0-.9 1-1c1-.1 1 .5 1 1s-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M160.5 171.8q0-.9 1-1c1-.1 1 .5 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M163.5 170.3q0-1 1-1c1 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M163.5 170.3q0-1 1-1c1 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M166.6 169.1q.1-.9 1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M166.6 169.1q.1-.9 1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M170 168.5q.1-.9 1.1-1a1 1 0 0 1 0 2q-1 0-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M170 168.5q.1-.9 1.1-1a1 1 0 0 1 0 2q-1 0-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M173.4 168.3q0-.9 1-1c1-.1 1 .5 1 1s-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M173.4 168.3q0-.9 1-1c1-.1 1 .5 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M176.6 168.5q.1-1 1-1t1.1 1c.1 1-.5 1-1 1a1 1 0 0 1-1.1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M176.6 168.5q.1-1 1-1t1.1 1c.1 1-.5 1-1 1a1 1 0 0 1-1.1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M179.8 168.5q.1-1 1-1 1.1 0 1.2 1c.1 1-.5 1-1.1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M179.8 168.5q.1-1 1-1 1.1 0 1.2 1c.1 1-.5 1-1.1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M178.2 171.2q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1m-.7 3.1q0-1 1-1c1 0 1 .4 1 1q0 .9-1 1a1 1 0 0 1-1-1m-.2 3q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1m.9 2.8q.1-.9 1-1 1 .1 1.1 1-.1 1-1 1a1 1 0 0 1-1.1-1m1.8 2.6q.1-.9 1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1"
                    />
                    <path
                        fill="#fff"
                        d="M182.3 166.5q.1-.9 1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M182.3 166.5q.1-.9 1-1a1 1 0 0 1 0 2 1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M185.2 165q0-1 1-1c1 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M185.2 165q0-1 1-1c1 0 1 .4 1 1s-.4 1-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M188.3 164q.1-1 1-1t1.1 1q0 .9-1 1c-1 .1-1-.5-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M188.3 164q.1-1 1-1t1.1 1q0 .9-1 1c-1 .1-1-.5-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M191.6 163.4q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M191.6 163.4q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M194.9 163.5q0-1 1-1c1 0 1 .4 1 1q0 .9-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M194.9 163.5q0-1 1-1c1 0 1 .4 1 1q0 .9-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M198.2 164q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M198.2 164q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#fff"
                        d="M201.3 165.1q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M201.3 165.1q.1-.9 1-1 1 .1 1.1 1 0 1.2-1 1a1 1 0 0 1-1-1z"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M174.7 228.9h-1v-1h-1.5v3.6h1.6v2.5h-3.4v7h1.8v14.3h-3.5v7.3h27.2v-7.3h-3.5V241h1.8v-7h-3.4v-2.5h1.6V228h-1.6v.9h-.8v-1h-1.6v1h-1.1v-1h-1.6v3.6h1.6v2.5H184v-7.8h1.7v-3.5H184v.9h-1v-1h-1.5v1h-.9v-1H179v3.6h1.7v7.8h-3.3v-2.5h1.6V228h-1.6v.9h-.9v-1h-1.8zm-6 33.7H196m-27.3-1.8H196m-27.3-1.8H196m-27.3-1.7H196m-27.3-2H196m-23.8-1.6h20.2m-20.2-1.8h20.2m-20.2-2h20.2m-20.2-1.7h20.2m-20.2-1.8h20.2m-20.2-1.8h20.2m-20.2-1.7h20.2m-22-1.8h23.8m-23.8-1.8h23.8m-23.8-1.8h23.8m-23.8-1.8h23.8m-20.4-1.7h17m-10.2-1.8h3.4m-3.4-1.8h3.4m-3.4-1.8h3.4m-3.4-1.7h3.4m-5.1-2.2h6.8m-12 7.5h3.6m-5-2.2h6.6m-6.7 32.6v-1.8m0-1.8v-1.7m-1.8 1.7v1.8m3.4 0V259m1.7 3.6v-1.8m0-1.8v-1.7m0-2v-1.6m0-1.8v-2m-1.7 7.4v-2m-3.4 2v-2m7 0v2m1.5-2v-1.6m-5.1-1.8v1.8m3.5-1.8v1.8m3.3-1.8v1.8M179 252v-2m1.7-1.7v1.7m0-5.3v1.8m-1.7-3.6v1.8m1.7-3.5v1.7m-3.3-1.7v1.7m-3.5-1.7v1.7m-1.6-3.5v1.8m3.3-1.8v1.8m3.4-1.8v1.8m1.7-3.6v1.8m-3.3-1.8v1.8m-3.5-1.8v1.8m-1.6-3.6v1.8m6.7-1.8v1.8m-3.4-5.3v1.8m15.3-1.8h-3.5m5-2.2h-6.6m6.7 32.6v-1.8m0-1.8v-1.7m1.8 1.7v1.8m-3.4 0V259m-1.7 3.6v-1.8m0-1.8v-1.7m0-2v-1.6m0-1.8v-2m1.7 7.4v-2m3.4 2v-2m-7 0v2m-1.5-2v-1.6m5.1-1.8v1.8m-3.5-1.8v1.8m-3.3-1.8v1.8m1.7-1.8v-2m-1.7-1.7v1.7m0-5.3v1.8m1.7-3.6v1.8m-1.7-3.5v1.7m3.3-1.7v1.7m3.5-1.7v1.7m1.6-3.5v1.8m-3.3-1.8v1.8m-3.4-1.8v1.8m-1.7-3.6v1.8m3.3-1.8v1.8m3.5-1.8v1.8m1.6-3.6v1.8m-6.7-1.8v1.8m3.4-5.3v1.8m-7 18v-2m0-5.4v-1.8m0 5.4v-1.8m0-5.3v-1.8m0-1.8v-1.7m0-3.6v-1.8m0-1.7v-1.8m-8.3 4.6h3.5m3.3-5.3h3.4m3.3 5.3h3.5"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".4"
                        d="M186.8 262.6v-4.7c0-.8-.4-3.5-4.6-3.5-4 0-4.4 2.7-4.4 3.5v4.7z"
                    />
                    <path
                        fill="#c8b100"
                        stroke="#000"
                        strokeWidth=".4"
                        d="m179.3 258.2-2.2-.3c0-.9.2-2.2.9-2.6l2 1.5c-.3.2-.7 1-.7 1.4zm6 0 2.2-.3c0-.9-.2-2.2-.9-2.6l-2 1.5c.3.2.7 1 .7 1.4zm-2.2-2.3 1-2-2-.4-1.7.4 1.1 2zm-4.2-5.5v-4.9c0-1.3-1-2.4-2.5-2.4s-2.4 1-2.4 2.4v4.9zm6.8 0v-4.9c0-1.3 1-2.4 2.5-2.4s2.4 1 2.4 2.4v4.9zm-1.7-12 .4-4.4h-4.2l.2 4.4zm3.3 0-.4-4.4h4.4l-.5 4.4zm-10 0 .2-4.4h-4.2l.5 4.4z"
                    />
                    <path
                        fill="#0039f0"
                        d="M185.3 262.6v-4c0-.7-.5-2.7-3.1-2.7-2.4 0-2.9 2-2.9 2.7v4zm-6.9-12.7v-4.2c0-1-.6-2.2-2-2.2s-2 1.1-2 2.2v4.3h4zm7.8 0v-4.2c0-1 .7-2.2 2-2.2s2 1.1 2 2.2v4.3h-4z"
                    />
                    <path
                        fill="#ad1519"
                        d="M190.8 269.8c0-9.7 7-17.6 15.6-17.6s15.6 7.9 15.6 17.6-7 17.5-15.6 17.5-15.6-7.8-15.6-17.5"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".6"
                        d="M190.8 269.8c0-9.7 7-17.6 15.6-17.6s15.6 7.9 15.6 17.6-7 17.5-15.6 17.5-15.6-7.8-15.6-17.5z"
                    />
                    <path
                        fill="#005bbf"
                        d="M195.4 269.7c0-7 5-12.8 11-12.8s11 5.7 11 12.8-5 13-11 13-11-5.8-11-13"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".6"
                        d="M195.4 269.7c0-7 5-12.8 11-12.8s11 5.7 11 12.8-5 13-11 13-11-5.8-11-13z"
                    />
                    <path
                        fill="#c8b100"
                        d="M201.2 260.9s-1.3 1.4-1.3 2.7a6 6 0 0 0 .6 2.4q-.4-.8-1.4-.8-1.3.1-1.4 1.3l.2.8.5.9q.2-.5 1-.5 1.1 0 1 1v.2h-1.2v1h1l-.8 1.5 1-.4.8.9.8-.9 1 .4-.7-1.5h1v-1h-1.1v-.3a1 1 0 0 1 1-1q.6 0 1 .6l.4-1 .2-.7a1.4 1.4 0 0 0-1.4-1.3q-1 0-1.4.9s.6-1.2.6-2.5-1.4-2.7-1.4-2.7"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".3"
                        d="M201.2 260.9s-1.3 1.4-1.3 2.7a6 6 0 0 0 .6 2.4q-.4-.8-1.4-.8-1.3.1-1.4 1.3l.2.8.5.9q.2-.5 1-.5 1.1 0 1 1v.2h-1.2v1h1l-.8 1.5 1-.4.8.9.8-.9 1 .4-.7-1.5h1v-1h-1.1v-.3a1 1 0 0 1 1-1q.6 0 1 .6l.4-1 .2-.7a1.4 1.4 0 0 0-1.4-1.3q-1 0-1.4.9s.6-1.2.6-2.5-1.4-2.7-1.4-2.7z"
                    />
                    <path
                        fill="#c8b100"
                        d="M199.2 269.9h4.1v-1h-4.1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M199.2 269.9h4.1v-1h-4.1z"
                    />
                    <path
                        fill="#c8b100"
                        d="M211.4 260.9s-1.3 1.4-1.3 2.7.6 2.4.6 2.4q-.3-.8-1.4-.8-1.3.1-1.4 1.3l.2.8.5.9q.3-.5 1-.5a1 1 0 0 1 1 1v.2h-1.2v1h1l-.8 1.5 1-.4.8.9.8-.9 1 .4-.7-1.5h1v-1h-1.1v-.3a1 1 0 0 1 1-1q.7 0 1 .6l.4-1 .2-.7a1.4 1.4 0 0 0-1.4-1.3q-1 0-1.4.9s.6-1.2.6-2.5-1.4-2.7-1.4-2.7"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".3"
                        d="M211.4 260.9s-1.3 1.4-1.3 2.7.6 2.4.6 2.4q-.3-.8-1.4-.8-1.3.1-1.4 1.3l.2.8.5.9q.3-.5 1-.5a1 1 0 0 1 1 1v.2h-1.2v1h1l-.8 1.5 1-.4.8.9.8-.9 1 .4-.7-1.5h1v-1h-1.1v-.3a1 1 0 0 1 1-1q.7 0 1 .6l.4-1 .2-.7a1.4 1.4 0 0 0-1.4-1.3q-1 0-1.4.9s.6-1.2.6-2.5-1.4-2.7-1.4-2.7z"
                    />
                    <path
                        fill="#c8b100"
                        d="M209.4 269.9h4.1v-1h-4.1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M209.4 269.9h4.1v-1h-4.1z"
                    />
                    <path
                        fill="#c8b100"
                        d="M206.3 269.6s-1.3 1.5-1.3 2.8.6 2.4.6 2.4q-.3-.8-1.4-.9-1.3.1-1.4 1.4l.2.7.5 1q.2-.6 1-.6a1 1 0 0 1 1 1v.3h-1.2v1h1l-.8 1.5 1-.4.8.9.8-1 1 .5-.7-1.5h1v-1h-1.1v-.3a1 1 0 0 1 1-1q.6 0 .9.6l.5-1 .2-.7a1.4 1.4 0 0 0-1.4-1.4q-1 0-1.4 1s.6-1.2.6-2.5-1.4-2.7-1.4-2.7"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeLinejoin="round"
                        strokeWidth=".3"
                        d="M206.3 269.6s-1.3 1.5-1.3 2.8.6 2.4.6 2.4q-.3-.8-1.4-.9-1.3.1-1.4 1.4l.2.7.5 1q.2-.6 1-.6a1 1 0 0 1 1 1v.3h-1.2v1h1l-.8 1.5 1-.4.8.9.8-1 1 .5-.7-1.5h1v-1h-1.1v-.3a1 1 0 0 1 1-1q.6 0 .9.6l.5-1 .2-.7a1.4 1.4 0 0 0-1.4-1.4q-1 0-1.4 1s.6-1.2.6-2.5-1.4-2.7-1.4-2.7z"
                    />
                    <path
                        fill="#c8b100"
                        d="M204.3 278.6h4.1v-1h-4.1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M204.3 278.6h4.1v-1h-4.1z"
                    />
                    <path
                        fill="#c8b100"
                        d="M237.6 223.4h-.3l-.3.4q-.4.3-.8 0l-.1-.4h-.5q-.4-.3-.1-.7v-.5h-.3l-.1.2q-.3.4-.7.2v-.2h-.3c-.5.2-.7-1-.7-1.2l-.2.2s.2.7.1 1.2l-.3 1.2a9 9 0 0 1 2.9 1.6 9 9 0 0 1 2.2 2.3l1.2-.5c.6-.2 1.3-.2 1.3-.2l.2-.2c-.3 0-1.5.1-1.5-.4v-.2h-.2q-.3-.3 0-.7l.2-.1v-.3h-.3l-.2.1q-.4.5-.8 0l-.1-.4h-.5q-.4-.3 0-.8l.2-.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M237.6 223.4h-.3l-.3.4q-.4.3-.8 0l-.1-.4h-.5q-.4-.3-.1-.7v-.5h-.3l-.1.2q-.3.4-.7.2v-.2h-.3c-.5.2-.7-1-.7-1.2l-.2.2s.2.7.1 1.2l-.3 1.2a9 9 0 0 1 2.9 1.6 9 9 0 0 1 2.2 2.3l1.2-.5c.6-.2 1.3-.2 1.3-.2l.2-.2c-.3 0-1.5.1-1.5-.4v-.2h-.2q-.3-.3 0-.7l.2-.1v-.3h-.3l-.2.1q-.4.5-.8 0l-.1-.4h-.5q-.4-.3 0-.8l.2-.3z"
                    />
                    <path d="M235.4 224h.2v.3h-.1q-.1-.1 0-.2" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="M235.4 224h.2v.3h-.1q-.1-.1 0-.2z"
                    />
                    <path d="m236.3 224.8-.3-.2v-.2h.1l.4.3.3.2v.2h-.2z" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="m236.3 224.8-.3-.2v-.2h.1l.4.3.3.2v.2h-.2l-.3-.3"
                    />
                    <path d="m234.6 223.7-.2-.2s-.1 0 0-.1l.3.1.3.1v.2h-.1z" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="m234.6 223.7-.2-.2s-.1 0 0-.1l.3.1.3.1v.2h-.1l-.3-.1"
                    />
                    <path d="M233.7 223h.2v.2h-.2s-.1-.1 0-.2" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="M233.7 223h.2v.2h-.2s-.1-.1 0-.2z"
                    />
                    <path d="M237.3 225.5v-.2h-.3l.1.2z" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="M237.3 225.5v-.2h-.3l.1.2z"
                    />
                    <path d="m237.9 226.2.2.2h.1v-.2l-.2-.2-.2-.2h-.1v.2z" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="m237.9 226.2.2.2h.1v-.2l-.2-.2-.2-.2h-.1v.2l.2.2"
                    />
                    <path d="M238.8 227v-.3h-.3v.2h.3" />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="M238.8 227v-.3h-.3v.2h.3z"
                    />
                    <path
                        fill="#c8b100"
                        d="M236.2 221.1h-.6l-.1.9v.1h.2l.7-.5-.3-.5"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M236.2 221.1h-.6l-.1.9v.1h.2l.7-.5-.3-.5"
                    />
                    <path
                        fill="#c8b100"
                        d="M234.6 221.6v.5l.9.1h.1v-.2l-.5-.7z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M234.6 221.6v.5l.9.1h.1v-.2l-.5-.7-.5.3"
                    />
                    <path
                        fill="#c8b100"
                        d="m236.4 222.6-.4.3-.6-.7v-.1h1.1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m236.4 222.6-.4.3-.6-.7v-.1h1.1v.5"
                    />
                    <path
                        fill="#c8b100"
                        d="M235.3 222h.4v.3h-.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M235.3 222h.4v.3h-.3z"
                    />
                    <path
                        fill="#c8b100"
                        d="m233.2 221.1-.2-.7-.4-.4s.4-.2.8.1 0 .9 0 .9z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m233.2 221.1-.2-.7-.4-.4s.4-.2.8.1 0 .9 0 .9z"
                    />
                    <path
                        fill="#c8b100"
                        d="m234.2 221.4-.4.4-.6-.6v-.2h1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m234.2 221.4-.4.4-.6-.6v-.2h1v.4"
                    />
                    <path
                        fill="#c8b100"
                        d="m233.1 221 .3-.1v.3q0 .3-.2.2z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m233.1 221 .3-.1v.3q0 .3-.2.2z"
                    />
                    <path
                        fill="#c8b100"
                        d="M238.3 222.5h-.5l-.3.7v.2h.2l.8-.4z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M238.3 222.5h-.5l-.3.7v.2h.2l.8-.4-.2-.5"
                    />
                    <path
                        fill="#c8b100"
                        d="M236.7 222.8v.5l.8.2h.1v-.2l-.4-.7z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M236.7 222.8v.5l.8.2h.1v-.2l-.4-.7-.5.2"
                    />
                    <path
                        fill="#c8b100"
                        d="m238.4 224-.5.2-.4-.7v-.2h.1l.9.2z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m238.4 224-.5.2-.4-.7v-.2h.1l.9.2-.1.5"
                    />
                    <path
                        fill="#c8b100"
                        d="M237.3 223.2h.4v.4h-.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M237.3 223.2h.4v.4h-.3z"
                    />
                    <path
                        fill="#c8b100"
                        d="m240.2 224.3.1.5-.8.3h-.2v-.2l.4-.8z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m240.2 224.3.1.5-.8.3h-.2v-.2l.4-.8.5.2"
                    />
                    <path
                        fill="#c8b100"
                        d="m240 225.8-.5.1-.3-.8v-.1h.2l.8.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m240 225.8-.5.1-.3-.8v-.1h.2l.8.3-.1.5"
                    />
                    <path
                        fill="#c8b100"
                        d="m238.6 224.3-.2.5.9.3h.1v-.1l-.3-.8z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m238.6 224.3-.2.5.9.3h.1v-.1l-.3-.8-.5.1"
                    />
                    <path
                        fill="#c8b100"
                        d="M239.5 225.2v-.3h-.4v.3z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M239.5 225.2v-.3h-.4v.3z"
                    />
                    <path
                        fill="#c8b100"
                        d="M240.8 227h.8l.5.3s.1-.4-.3-.7c-.3-.3-.8.2-.8.2z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M240.8 227h.8l.5.3s.1-.4-.3-.7c-.3-.3-.8.2-.8.2z"
                    />
                    <path
                        fill="#c8b100"
                        d="m240.3 226.1-.3.5.8.5v-.1h.2l-.1-1z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="m240.3 226.1-.3.5.8.5v-.1h.2l-.1-1-.6.1"
                    />
                    <path
                        fill="#c8b100"
                        d="M241 227s.1-.1 0-.2h-.3q-.3 0-.1.2z"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".3"
                        d="M241 227s.1-.1 0-.2h-.3q-.3 0-.1.2zm38-21.9v.6h-2.4v-.6h1v-1.3h-.7v-.5h.6v-.6h.6v.6h.6v.6h-.6v1.2h1"
                    />
                    <path
                        fill="none"
                        d="M134.4 217.1v-1.2m-.4 1.2v-1.2m-.2 1.2v-1.2m-.3 1.2v-1.2"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="M133.2 217.1v-1.2m-.5 1.1v-1m.2 1v-1m-.7 1v-1m.2 1v-1m-.9 1v-1m.2 1v-1m.3 1v-1m-.7 1v-1m-.3.9v-.8m-.1.8v-.8m-.5.7v-.6m.2.6v-.6m-.4.5v-.5m-.2.5v-.4m-.3.3v-.3m-.3.3v-.2"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".2"
                        d="M129.2 216.6v-.2"
                    />
                    <path
                        fill="none"
                        d="M135.7 217v-1m-.5 1v-1m-.4 1.2V216m143 1.1V216m-.4 1.1V216m-.3 1.1V216m-.3 1.2V216"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".1"
                        d="M276.6 217.1V216m-.6 1v-1m.3 1v-1m-.8 1v-1m.3 1v-1m-.9 1v-1m.2 1v-1m.2 1v-1m-.6 1v-1m-.3.9v-.8m-.2.8v-.8m-.4.7v-.6m.2.6v-.6m-.5.6v-.6m-.2.5v-.4m-.3.4v-.4m-.2.3v-.2"
                    />
                    <path
                        fill="none"
                        stroke="#000"
                        strokeWidth=".2"
                        d="M272.6 216.6v-.2"
                    />
                    <path
                        fill="none"
                        d="M279.1 217v-1m-.6 1v-1m-.4 1.1V216"
                    />
                </svg>
            )
        case LocaleEnum.German:
            return (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    id="flag-icons-de"
                    viewBox="0 0 640 480"
                    className={className}
                    {...props}
                >
                    <path
                        fill="#fc0"
                        d="M0 320h640v160H0z"
                    />
                    <path
                        fill="#000001"
                        d="M0 0h640v160H0z"
                    />
                    <path
                        fill="red"
                        d="M0 160h640v160H0z"
                    />
                </svg>
            )
        case LocaleEnum.Italian:
            return (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    id="flag-icons-it"
                    viewBox="0 0 640 480"
                    className={className}
                    {...props}
                >
                    <g
                        fillRule="evenodd"
                        strokeWidth="1pt"
                    >
                        <path
                            fill="#fff"
                            d="M0 0h640v480H0z"
                        />
                        <path
                            fill="#009246"
                            d="M0 0h213.3v480H0z"
                        />
                        <path
                            fill="#ce2b37"
                            d="M426.7 0H640v480H426.7z"
                        />
                    </g>
                </svg>
            )
        case LocaleEnum.French:
            return (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    id="flag-icons-fr"
                    viewBox="0 0 640 480"
                    className={className}
                    {...props}
                >
                    <path
                        fill="#fff"
                        d="M0 0h640v480H0z"
                    />
                    <path
                        fill="#000091"
                        d="M0 0h213.3v480H0z"
                    />
                    <path
                        fill="#e1000f"
                        d="M426.7 0H640v480H426.7z"
                    />
                </svg>
            )
        case LocaleEnum.Portuguese_Portugal:
            return (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    id="flag-icons-pt"
                    viewBox="0 0 640 480"
                    className={className}
                    {...props}
                >
                    <path
                        fill="red"
                        d="M256 0h384v480H256z"
                    />
                    <path
                        fill="#060"
                        d="M0 0h256v480H0z"
                    />
                    <g
                        fill="#ff0"
                        fillRule="evenodd"
                        stroke="#000"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth=".6"
                    >
                        <path d="M339.5 306.2c-32.3-1-180-93.2-181-108l8.1-13.5c14.7 21.3 165.7 111 180.6 107.8z" />
                        <path d="M164.9 182.8c-2.9 7.8 38.6 33.4 88.4 63.8s92.9 49 96 46.4l1.5-2.8q-.9 1.6-4.3.6c-13.5-3.9-48.6-20-92.1-46.4-43.6-26.4-81.4-50.7-87.3-61a6 6 0 0 1-.6-3.1h-.2l-1.2 2.2zm175.3 123.8q-.7 1.3-3.5.8c-12-1.3-48.6-19.1-91.9-45-50.4-30.2-92-57.6-87.4-64.8l1.2-2.2.2.1c-4 12.2 82.1 61.4 87.2 64.6 49.8 30.8 91.8 48.9 95.5 44.2z" />
                        <path d="M256.2 207.2c32.2-.3 72-4.4 95-13.6l-5-8c-13.5 7.5-53.5 12.5-90.3 13.2-43.4-.4-74.1-4.5-89.5-14.8l-4.6 8.6c28.2 12 57.2 14.5 94.4 14.6" />
                        <path d="M352.5 193.8c-.8 1.3-15.8 6.4-37.8 10.2a381 381 0 0 1-58.6 4.3 416 416 0 0 1-56.2-3.6c-23.1-3.6-35-8.6-39.5-10.4l1.1-2.2c12.7 5 24.7 8 38.7 10.2A412 412 0 0 0 256 206a392 392 0 0 0 58.3-4.3c22.5-3.7 34.8-8.4 36.6-10.5zm-4.4-8.1c-2.4 2-14.6 6.3-36 9.7a388 388 0 0 1-55.8 4c-22 0-40.1-1.6-53.8-3.6-21.8-2.8-33.4-8-37.6-9.4l1.3-2.2c3.3 1.7 14.4 6.2 36.5 9.3a385 385 0 0 0 53.6 3.4 384 384 0 0 0 55.4-4c21.5-3 33.1-8.4 34.9-9.8zM150.3 246c19.8 10.7 63.9 16 105.6 16.4 38 .1 87.4-5.8 105.9-15.6l-.5-10.7c-5.8 9-58.8 17.7-105.8 17.4s-90.7-7.6-105.3-17v9.5" />
                        <path d="M362.8 244.5v2.5c-2.8 3.4-20.2 8.4-42 12a434 434 0 0 1-65.4 4.4 400 400 0 0 1-62-4.3 155 155 0 0 1-44.4-12v-2.9c9.7 6.4 35.9 11.2 44.7 12.6 15.8 2.4 36.1 4.2 61.7 4.2 26.9 0 48.4-1.9 65-4.4 15.7-2.3 38-8.2 42.4-12.1m0-9v2.5c-2.8 3.3-20.2 8.3-42 11.9a434 434 0 0 1-65.4 4.5 414 414 0 0 1-62-4.3 155 155 0 0 1-44.4-12v-3c9.7 6.5 36 11.2 44.7 12.6a408 408 0 0 0 61.7 4.3c26.9 0 48.5-2 65-4.5 15.7-2.2 38-8.1 42.4-12m-107 68.8c-45.6-.2-84.7-12.4-93-14.4l6 9.4a250 250 0 0 0 87.4 14.3c34.7-1 65-3.7 86.3-14.1l6.2-9.8c-14.5 6.9-64 14.6-93 14.6" />
                        <path d="m344.9 297.3-2.8 4c-10 3.6-26 7.4-32.6 8.4a296 296 0 0 1-53.7 5c-40.4-.6-73.5-8.5-89-15.3l-1.3-2.1.2-.4 2.1.9a287 287 0 0 0 88.2 14.5c18.8 0 37.5-2.1 52.6-4.8 23.2-4.7 32.6-8.2 35.5-9.8l.7-.4zm5.3-8.8-2 3.5c-5.4 2-20 6.2-41.3 9.2-14 1.9-22.7 3.8-50.6 4.3a347 347 0 0 1-94.2-14L161 289a390 390 0 0 0 95.4 14c25.5-.5 36.4-2.4 50.3-4.3 24.8-3.8 37.3-8 41-9.1v-.2l2.6-1z" />
                        <path d="M350.8 237.6c.1 30-15.3 57-27.6 68.8a99 99 0 0 1-67.8 28.2c-30.3.5-58.8-19.2-66.5-27.9a101 101 0 0 1-27.5-67.4c1.8-32.8 14.7-55.6 33.3-71.3a100 100 0 0 1 64.2-22.7 98 98 0 0 1 71 35.6c12.5 15.2 18 31.7 20.9 56.7M255.6 135a106 106 0 0 1 106 105.2 105.6 105.6 0 1 1-211.4 0c-.1-58 47.3-105.2 105.4-105.2" />
                        <path d="M255.9 134.5c58.2 0 105.6 47.4 105.6 105.6S314.1 345.7 256 345.7s-105.6-47.4-105.6-105.6S197.8 134.5 256 134.5zM152.6 240c0 56.8 46.7 103.3 103.3 103.3S359.2 296.8 359.2 240s-46.7-103.3-103.3-103.3S152.6 183.2 152.6 240" />
                        <path d="M256 143.3a97 97 0 0 1 96.7 96.7 97 97 0 0 1-96.7 96.8c-53 0-96.7-43.6-96.7-96.8a97 97 0 0 1 96.7-96.7M161.6 240c0 52 42.6 94.4 94.4 94.4s94.4-42.5 94.4-94.4-42.6-94.4-94.4-94.4a95 95 0 0 0-94.4 94.4" />
                        <path d="M260.3 134h-9.1v212.3h9z" />
                        <path d="M259.3 132.8h2.3v214.7h-2.2V132.8zm-9 0h2.4v214.7h-2.3z" />
                        <path d="M361.6 244.2v-7.8l-6.4-6-36.3-9.6-52.2-5.3-63 3.2-44.8 10.6-9 6.7v7.9l22.9-10.3 54.4-8.5h52.3l38.4 4.2 26.6 6.4z" />
                        <path d="M256 223.8c24.9 0 49 2.3 68.3 6 19.8 4 33.7 9 38.5 14.5v2.8c-5.8-7-24.5-12-39-15-19-3.6-43-6-67.9-6-26.1 0-50.5 2.6-69.3 6.2-15 3-35.1 9-37.6 14.8v-2.9c1.3-4 16.3-10 37.3-14.3 18.9-3.7 43.3-6.1 69.6-6.1zm0-9.1a383 383 0 0 1 68.3 6c19.8 4 33.7 9 38.5 14.6v2.7c-5.8-6.9-24.5-12-39-14.9-19-3.7-43-6-67.9-6a376 376 0 0 0-69.2 6.2c-14.5 2.7-35.4 8.9-37.7 14.7v-2.8c1.4-4 16.6-10.3 37.3-14.3 19-3.7 43.3-6.2 69.7-6.2m-.6-46.2c39.3-.2 73.6 5.5 89.3 13.5l5.7 10c-13.6-7.4-50.6-15-94.9-14-36.1.3-74.7 4-94 14.4l6.8-11.4c15.9-8.3 53.3-12.5 87.1-12.5" />
                        <path d="M256 176.7a354 354 0 0 1 61.3 4.3c16 3 31.3 7.4 33.5 9.8l1.7 3c-5.3-3.4-18.6-7.3-35.6-10.5s-38.7-4.3-61-4.2c-25.3-.1-45 1.2-61.8 4.2a109 109 0 0 0-33.3 10.3l1.7-3.1c6-3 15.3-6.7 31.1-9.6 17.5-3.2 37.4-4.1 62.4-4.2m0-9c21.4-.2 42.6 1 59.1 4a96 96 0 0 1 30.6 10l2.5 4c-4.2-4.7-20-9.2-34.1-11.6-16.4-2.9-36.7-4-58.1-4.2a361 361 0 0 0-59.5 4.4 97 97 0 0 0-29.6 9.1l2.2-3.3c5.8-3 15.2-5.8 27-8.1a357 357 0 0 1 59.9-4.4zM308.4 284a276 276 0 0 0-52.5-4c-65.5.8-86.6 13.5-89.2 17.3l-5-8c16.8-12 52.4-18.8 94.6-18.2q32.9.5 56.6 5l-4.5 8" />
                        <path d="M255.6 278.9c18.2.3 36 1 53.3 4.2l-1.2 2.2c-16-3-33.2-4-52-4-24.3-.2-48.7 2.1-70 8.2-6.7 1.9-17.8 6.2-19 9.8l-1.2-2c.4-2.2 7-6.6 19.6-10 24.4-7 47.2-8.3 70.5-8.4m.8-9.2a327 327 0 0 1 57.3 5l-1.3 2.3a299 299 0 0 0-56-4.9c-24.2 0-49.9 1.8-73.3 8.6-7.5 2.2-20.6 7-21 10.7l-1.2-2.2c.2-3.4 11.5-7.9 21.7-10.8 23.5-6.9 49.3-8.6 73.8-8.7" />
                        <path d="m349.4 290.5-7.8 12.3-22.7-20.1-58.6-39.5-66.2-36.3-34.3-11.7 7.3-13.6 2.5-1.3 21.3 5.3 70.4 36.3 40.6 25.6L336 272l13.9 16z" />
                        <path d="M158.6 195.5c6-4 50.2 15.6 96.6 43.6 46.1 28 90.3 59.6 86.3 65.5l-1.3 2.1-.6.5c.1-.1.8-1 0-3.1-2-6.5-33.4-31.5-85.3-62.9-50.7-30.1-92.9-48.3-97-43.1zM351 290.4c3.8-7.6-37.2-38.5-88.1-68.6-52-29.5-89.6-46.9-96.5-41.7L165 183l.4-.5c1.2-1 3.3-1 4.2-1 11.8.2 45.5 15.7 92.8 42.8 20.8 12 87.6 55 87.3 67 0 1 .1 1.2-.3 1.8l1.7-2.6z" />
                    </g>
                    <g transform="translate(0 26.7)scale(1.06667)">
                        <path
                            fill="#fff"
                            stroke="#000"
                            strokeWidth=".7"
                            d="M180.6 211a59 59 0 0 0 17.5 41.7 59 59 0 0 0 41.8 17.6 59 59 0 0 0 42-17.4 59 59 0 0 0 17.4-41.8v-79.2l-118.7-.2z"
                        />
                        <path
                            fill="red"
                            stroke="#000"
                            strokeWidth=".5"
                            d="M183 211.1a56 56 0 0 0 16.8 40 57 57 0 0 0 40.2 16.8 57 57 0 0 0 40.2-16.6 56 56 0 0 0 16.7-40v-77H183v76.8m91-53.7v48.9l-.1 5.1a33 33 0 0 1-10 24 34 34 0 0 1-24 10c-9.4 0-17.7-4-23.9-10.2a34 34 0 0 1-10-24v-54z"
                        />
                        <g id="pt-e">
                            <g
                                id="pt-d"
                                fill="#ff0"
                                stroke="#000"
                                strokeWidth=".5"
                            >
                                <path
                                    stroke="none"
                                    d="M190.2 154.4c.1-5.5 4-6.8 4-6.8.1 0 4.3 1.4 4.3 6.9z"
                                />
                                <path d="m186.8 147.7-.7 6.3h4.2c0-5.2 4-6 4-6 .1 0 4 1.1 4.1 6h4.2l-.8-6.4zm-1 6.4h17q.5 0 .6.7 0 .8-.6.8h-17q-.5 0-.6-.8 0-.7.7-.7z" />
                                <path d="M192 154c0-3.3 2.3-4.2 2.3-4.2s2.3 1 2.3 4.2H192m-5.8-9h16.3q.5.1.6.8 0 .5-.6.6h-16.3q-.5 0-.6-.7 0-.5.6-.6zm.4 1.5H202q.5 0 .6.7t-.6.7h-15.5q-.6 0-.6-.7t.6-.7zm5-10.6h1.2v.8h.9v-.8h1.3v.9h.9v-1h1.2v2q0 .6-.5.6h-4.4q-.5 0-.6-.5zm4.6 2.7.3 6.4h-4.3l.3-6.5h3.7" />
                                <path
                                    id="pt-a"
                                    d="M191 141.6v3.4h-4v-3.4z"
                                />
                                <use
                                    xlinkHref="#pt-a"
                                    width="100%"
                                    height="100%"
                                    x="10.6"
                                />
                                <path
                                    id="pt-b"
                                    d="M186.3 139h1.2v1h.9v-1h1.2v1h.9v-1h1.2v2q0 .6-.5.6h-4.3l-.6-.6z"
                                />
                                <use
                                    xlinkHref="#pt-b"
                                    width="100%"
                                    height="100%"
                                    x="10.6"
                                />
                                <path
                                    fill="#000001"
                                    stroke="none"
                                    d="M193.9 140.6c0-.6.9-.6.9 0v1.6h-.9z"
                                />
                                <path
                                    id="pt-c"
                                    fill="#000001"
                                    stroke="none"
                                    d="M188.6 142.8c0-.6.8-.6.8 0v1.2h-.8z"
                                />
                                <use
                                    xlinkHref="#pt-c"
                                    width="100%"
                                    height="100%"
                                    x="10.6"
                                />
                            </g>
                            <use
                                xlinkHref="#pt-d"
                                width="100%"
                                height="100%"
                                y="46.3"
                            />
                            <use
                                xlinkHref="#pt-d"
                                width="100%"
                                height="100%"
                                transform="rotate(-45.2 312.8 180)"
                            />
                        </g>
                        <use
                            xlinkHref="#pt-d"
                            width="100%"
                            height="100%"
                            x="45.7"
                        />
                        <use
                            xlinkHref="#pt-e"
                            width="100%"
                            height="100%"
                            transform="matrix(-1 0 0 1 479.8 0)"
                        />
                        <g
                            id="pt-f"
                            fill="#fff"
                        >
                            <path
                                fill="#039"
                                d="M232.6 202.4a8 8 0 0 0 2.2 5.7 7 7 0 0 0 5.3 2.4q3.2-.2 5.3-2.4a8 8 0 0 0 2.2-5.7v-10.8h-15z"
                            />
                            <circle
                                cx="236.1"
                                cy="195.7"
                                r="1.5"
                            />
                            <circle
                                cx="244.4"
                                cy="195.7"
                                r="1.5"
                            />
                            <circle
                                cx="240.2"
                                cy="199.7"
                                r="1.5"
                            />
                            <circle
                                cx="236.1"
                                cy="203.9"
                                r="1.5"
                            />
                            <circle
                                cx="244.4"
                                cy="203.9"
                                r="1.5"
                            />
                        </g>
                        <use
                            xlinkHref="#pt-f"
                            width="100%"
                            height="100%"
                            y="-26"
                        />
                        <use
                            xlinkHref="#pt-f"
                            width="100%"
                            height="100%"
                            x="-20.8"
                        />
                        <use
                            xlinkHref="#pt-f"
                            width="100%"
                            height="100%"
                            x="20.8"
                        />
                        <use
                            xlinkHref="#pt-f"
                            width="100%"
                            height="100%"
                            y="25.8"
                        />
                    </g>
                </svg>
            )
        case LocaleEnum.Portuguese_Brazil:
            return (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    id="flag-icons-br"
                    viewBox="0 0 640 480"
                    className={className}
                    {...props}
                >
                    <g strokeWidth="1pt">
                        <path
                            fill="#229e45"
                            fillRule="evenodd"
                            d="M0 0h640v480H0z"
                        />
                        <path
                            fill="#f8e509"
                            fillRule="evenodd"
                            d="m321.4 436 301.5-195.7L319.6 44 17.1 240.7z"
                        />
                        <path
                            fill="#2b49a3"
                            fillRule="evenodd"
                            d="M452.8 240c0 70.3-57.1 127.3-127.6 127.3A127.4 127.4 0 1 1 452.8 240"
                        />
                        <path
                            fill="#ffffef"
                            fillRule="evenodd"
                            d="m283.3 316.3-4-2.3-4 2 .9-4.5-3.2-3.4 4.5-.5 2.2-4 1.9 4.2 4.4.8-3.3 3m86 26.3-3.9-2.3-4 2 .8-4.5-3.1-3.3 4.5-.5 2.1-4.1 2 4.2 4.4.8-3.4 3.1m-36.2-30-3.4-2-3.5 1.8.8-3.9-2.8-2.9 4-.4 1.8-3.6 1.6 3.7 3.9.7-3 2.7m87-8.5-3.4-2-3.5 1.8.8-3.9-2.7-2.8 3.9-.4 1.8-3.5 1.6 3.6 3.8.7-2.9 2.6m-87.3-22-4-2.2-4 2 .8-4.6-3.1-3.3 4.5-.5 2.1-4.1 2 4.2 4.4.8-3.4 3.2m-104.6-35-4-2.2-4 2 1-4.6-3.3-3.3 4.6-.5 2-4.1 2 4.2 4.4.8-3.3 3.1m13.3 57.2-4-2.3-4 2 .9-4.5-3.2-3.3 4.5-.6 2.1-4 2 4.2 4.4.8-3.3 3.1m132-67.3-3.6-2-3.6 1.8.8-4-2.8-3 4-.5 1.9-3.6 1.7 3.8 4 .7-3 2.7m-6.7 38.3-2.7-1.6-2.9 1.4.6-3.2-2.2-2.3 3.2-.4 1.5-2.8 1.3 3 3 .5-2.2 2.2m-142.2 50.4-2.7-1.5-2.7 1.3.6-3-2.1-2.2 3-.4 1.4-2.7 1.3 2.8 3 .6-2.3 2M419 299.8l-2.2-1.1-2.2 1 .5-2.3-1.7-1.6 2.4-.3 1.2-2 1 2 2.5.5-1.9 1.5"
                        />
                        <path
                            fill="#ffffef"
                            fillRule="evenodd"
                            d="m219.3 287.6-2.7-1.5-2.7 1.3.6-3-2.1-2.2 3-.4 1.4-2.7 1.3 2.8 3 .6-2.3 2"
                        />
                        <path
                            fill="#ffffef"
                            fillRule="evenodd"
                            d="m219.3 287.6-2.7-1.5-2.7 1.3.6-3-2.1-2.2 3-.4 1.4-2.7 1.3 2.8 3 .6-2.3 2m42.3 3-2.6-1.4-2.7 1.3.6-3-2.1-2.2 3-.4 1.4-2.7 1.3 2.8 3 .5-2.3 2.1m-4.8 17-2.6-1.5-2.7 1.4.6-3-2.1-2.3 3-.4 1.4-2.7 1.3 2.8 3 .6-2.3 2m87.4-22.2-2.6-1.6-2.8 1.4.6-3-2-2.3 3-.3 1.4-2.7 1.2 2.8 3 .5-2.2 2.1m-25.1 3-2.7-1.5-2.7 1.4.6-3-2-2.3 3-.3 1.4-2.8 1.2 2.9 3 .5-2.2 2.1m-68.8-5.8-1.7-1-1.7.8.4-1.9-1.3-1.4 1.9-.2.8-1.7.8 1.8 1.9.3-1.4 1.3m167.8 45.4-2.6-1.5-2.7 1.4.6-3-2.1-2.3 3-.4 1.4-2.7 1.3 2.8 3 .6-2.3 2m-20.8 6-2.2-1.4-2.3 1.2.5-2.6-1.7-1.8 2.5-.3 1.2-2.3 1 2.4 2.5.4-1.9 1.8m10.4 2.3-2-1.2-2.1 1 .4-2.3-1.6-1.7 2.3-.3 1.1-2 1 2 2.3.5-1.7 1.6m29.1-22.8-2-1-2 1 .5-2.3-1.6-1.7 2.3-.3 1-2 1 2.1 2.1.4-1.6 1.6m-38.8 41.8-2.5-1.4-2.7 1.2.6-2.8-2-2 3-.3 1.3-2.5 1.2 2.6 3 .5-2.3 1.9m.6 14.2-2.4-1.4-2.4 1.3.6-2.8-1.9-2 2.7-.4 1.2-2.5 1.1 2.6 2.7.5-2 2m-19-23.1-1.9-1.2-2 1 .4-2.2-1.5-1.7 2.2-.2 1-2 1 2 2.2.4-1.6 1.6m-17.8 2.3-2-1.2-2 1 .5-2.2-1.6-1.7 2.3-.2 1-2 1 2 2.1.4-1.6 1.6m-30.4-24.6-2-1.1-2 1 .5-2.3-1.6-1.6 2.2-.3 1-2 1 2 2.2.5-1.6 1.5m3.7 57-1.6-.9-1.8.9.4-2-1.3-1.4 1.9-.2.9-1.7.8 1.8 1.9.3-1.4 1.3m-46.2-86.6-4-2.3-4 2 .9-4.5-3.2-3.3 4.5-.6 2.2-4 1.9 4.2 4.4.8-3.3 3.1"
                        />
                        <path
                            fill="#fff"
                            fillRule="evenodd"
                            d="M444.4 285.8a125 125 0 0 0 5.8-19.8c-67.8-59.5-143.3-90-238.7-83.7a125 125 0 0 0-8.5 20.9c113-10.8 196 39.2 241.4 82.6"
                        />
                        <path
                            fill="#309e3a"
                            d="m414 252.4 2.3 1.3a3 3 0 0 0-.3 2.2 3 3 0 0 0 1.4 1.7q1 .8 2 .7.9 0 1.3-.7l.2-.9-.5-1-1.5-1.8a8 8 0 0 1-1.8-3 4 4 0 0 1 2-4.4 4 4 0 0 1 2.3-.2 7 7 0 0 1 2.6 1.2q2.1 1.5 2.6 3.2a4 4 0 0 1-.6 3.3l-2.4-1.5q.5-1 .2-1.7-.2-.8-1.2-1.4a3 3 0 0 0-1.8-.7 1 1 0 0 0-.9.5q-.3.4-.1 1 .2.8 1.6 2.2t2 2.5a4 4 0 0 1-.3 4.2 4 4 0 0 1-1.9 1.5 4 4 0 0 1-2.4.3q-1.3-.3-2.8-1.3-2.2-1.5-2.7-3.3a5 5 0 0 1 .6-4zm-11.6-7.6 2.5 1.3a3 3 0 0 0-.2 2.2 3 3 0 0 0 1.4 1.6q1.1.8 2 .6.9 0 1.3-.8l.2-.8q0-.5-.5-1l-1.6-1.8q-1.7-1.6-2-2.8a4 4 0 0 1 .4-3.1 4 4 0 0 1 1.6-1.4 4 4 0 0 1 2.2-.3 7 7 0 0 1 2.6 1q2.3 1.5 2.7 3.1a4 4 0 0 1-.4 3.4l-2.5-1.4q.5-1 .2-1.7-.4-1-1.3-1.4a3 3 0 0 0-1.9-.6 1 1 0 0 0-.8.5q-.3.4-.1 1 .3.8 1.7 2.2 1.5 1.5 2 2.4a4 4 0 0 1 0 4.2 4 4 0 0 1-1.8 1.6 4 4 0 0 1-2.4.3 8 8 0 0 1-2.9-1.1 6 6 0 0 1-2.8-3.2 5 5 0 0 1 .4-4m-14.2-3.8 7.3-12 8.8 5.5-1.2 2-6.4-4-1.6 2.7 6 3.7-1.3 2-6-3.7-2 3.3 6.7 4-1.2 2zm-20.7-17 1.1-2 5.4 2.7-2.5 5q-1.2.3-3 .2a9 9 0 0 1-3.3-1 8 8 0 0 1-3-2.6 6 6 0 0 1-1-3.5 9 9 0 0 1 1-3.7 8 8 0 0 1 2.6-3 6 6 0 0 1 3.6-1.1q1.4 0 3.2 1 2.4 1.1 3.1 2.8a5 5 0 0 1 .3 3.5l-2.7-.8a3 3 0 0 0-.2-2q-.4-.9-1.6-1.4a4 4 0 0 0-3.1-.3q-1.5.5-2.6 2.6t-.7 3.8a4 4 0 0 0 2 2.4q.8.5 1.7.5h1.8l.8-1.6zm-90.2-22.3 2-14 4.2.7 1.1 9.8 3.9-9 4.2.6-2 13.8-2.7-.4 1.7-10.9-4.4 10.5-2.7-.4-1.1-11.3-1.6 11zm-14.1-1.7 1.3-14 10.3 1-.2 2.4-7.5-.7-.3 3 7 .7-.3 2.4-7-.7-.3 3.8 7.8.7-.2 2.4z"
                        />
                        <g strokeOpacity=".5">
                            <path
                                fill="#309e3a"
                                d="M216.5 191.3q0-2.2.7-3.6a7 7 0 0 1 1.4-1.9 5 5 0 0 1 1.8-1.2q1.5-.5 3-.5 3.1.1 5 2a7 7 0 0 1 1.6 5.5q0 3.3-2 5.3a7 7 0 0 1-5 1.7 7 7 0 0 1-4.8-2 7 7 0 0 1-1.7-5.3"
                            />
                            <path
                                fill="#f7ffff"
                                d="M219.4 191.3q0 2.3 1 3.6t2.8 1.3a4 4 0 0 0 2.8-1.1q1-1.2 1.1-3.7.1-2.4-1-3.6a4 4 0 0 0-2.7-1.3 4 4 0 0 0-2.8 1.2q-1.1 1.2-1.2 3.6"
                            />
                        </g>
                        <g strokeOpacity=".5">
                            <path
                                fill="#309e3a"
                                d="m233 198.5.2-14h6q2.2 0 3.2.5 1 .3 1.6 1.3c.6 1 .6 1.4.6 2.3a4 4 0 0 1-1 2.6 5 5 0 0 1-2.7 1.2l1.5 1.2q.6.6 1.5 2.3l1.7 2.8h-3.4l-2-3.2-1.4-2-.9-.6-1.4-.2h-.6v5.8z"
                            />
                            <path
                                fill="#fff"
                                d="M236 190.5h2q2.1 0 2.6-.2.5-.1.8-.5.4-.6.3-1 0-.9-.4-1.2-.3-.4-1-.6h-2l-2.3-.1z"
                            />
                        </g>
                        <g strokeOpacity=".5">
                            <path
                                fill="#309e3a"
                                d="m249 185.2 5.2.3q1.7 0 2.6.3a5 5 0 0 1 2 1.4 6 6 0 0 1 1.2 2.4q.4 1.4.3 3.3a9 9 0 0 1-.5 3q-.6 1.5-1.7 2.4a5 5 0 0 1-2 1q-1 .3-2.5.2l-5.3-.3z"
                            />
                            <path
                                fill="#fff"
                                d="m251.7 187.7-.5 9.3h3.8q.8 0 1.2-.5.5-.4.8-1.3t.4-2.6l-.1-2.5a3 3 0 0 0-.8-1.4l-1.2-.7-2.3-.3z"
                            />
                        </g>
                        <g strokeOpacity=".5">
                            <path
                                fill="#309e3a"
                                d="m317.6 210.2 3.3-13.6 4.4 1 3.2 1q1.1.6 1.6 1.9t.2 2.8q-.3 1.2-1 2a4 4 0 0 1-3 1.4q-1 0-3-.5l-1.7-.5-1.2 5.2z"
                            />
                            <path
                                fill="#fff"
                                d="m323 199.6-.8 3.8 1.5.4q1.6.4 2.2.3a2 2 0 0 0 1.6-1.5q0-.7-.2-1.3a2 2 0 0 0-1-.9l-1.9-.5-1.3-.3z"
                            />
                        </g>
                        <g strokeOpacity=".5">
                            <path
                                fill="#309e3a"
                                d="m330.6 214.1 4.7-13.2 5.5 2q2.2.8 3 1.4.8.7 1 1.8c.2 1.1.2 1.5 0 2.3q-.6 1.5-1.8 2.2-1.2.6-3 .3.6.7 1 1.6l.8 2.7.6 3.1-3.1-1.1-1-3.6-.7-2.4-.6-.8q-.3-.4-1.3-.7l-.5-.2-2 5.6z"
                            />
                            <path
                                fill="#fff"
                                d="m336 207.4 1.9.7q2 .7 2.5.7t.9-.3q.5-.3.6-.9.3-.6 0-1.2l-.8-.9-2-.7-2-.7-1.2 3.3z"
                            />
                        </g>
                        <g strokeOpacity=".5">
                            <path
                                fill="#309e3a"
                                d="M347 213.6a9 9 0 0 1 1.7-3.2l1.8-1.5 2-.7q1.5-.1 3.1.4a7 7 0 0 1 4.2 3.3q1.2 2.4.2 5.7a7 7 0 0 1-3.4 4.5q-2.3 1.3-5.2.4a7 7 0 0 1-4.2-3.3 7 7 0 0 1-.2-5.6"
                            />
                            <path
                                fill="#fff"
                                d="M349.8 214.4q-.7 2.3 0 3.8c.7 1.5 1.2 1.6 2.3 2q1.5.5 3-.4 1.4-.8 2.1-3.2.8-2.2 0-3.7a4 4 0 0 0-2.2-2 4 4 0 0 0-3 .3q-1.5.8-2.2 3.2"
                            />
                        </g>
                        <g strokeOpacity=".5">
                            <path
                                fill="#309e3a"
                                d="m374.3 233.1 6.4-12.4 5.3 2.7a10 10 0 0 1 2.7 1.9q.8.7.8 1.9c0 1.2 0 1.5-.4 2.2a4 4 0 0 1-2 2q-1.5.4-3.1-.2.6 1 .8 1.7.3.9.4 2.8l.2 3.2-3-1.5-.4-3.7-.3-2.5-.5-1-1.2-.7-.5-.3-2.7 5.2z"
                            />
                            <path
                                fill="#fff"
                                d="m380.5 227.2 1.9 1q1.8 1 2.3 1t1-.2q.4-.2.7-.8t.2-1.2l-.7-1-1.8-1-2-1z"
                            />
                        </g>
                        <g strokeOpacity=".5">
                            <path
                                fill="#309e3a"
                                d="M426.1 258.7a9 9 0 0 1 2.5-2.6 7 7 0 0 1 2.2-.9 6 6 0 0 1 2.2 0q1.5.3 2.8 1.2a7 7 0 0 1 3 4.4q.4 2.6-1.4 5.5a7 7 0 0 1-4.5 3.3 7 7 0 0 1-5.2-1.1 7 7 0 0 1-3-4.4q-.4-2.7 1.4-5.4"
                            />
                            <path
                                fill="#fff"
                                d="M428.6 260.3q-1.4 2-1.1 3.6a4 4 0 0 0 1.6 2.5q1.5 1 3 .6t2.9-2.4q1.4-2.1 1.1-3.6t-1.6-2.6c-1.4-1.1-2-.8-3-.5q-1.5.3-3 2.4z"
                            />
                        </g>
                        <path
                            fill="#309e3a"
                            d="m301.8 204.5 2.3-9.8 7.2 1.7-.3 1.6-5.3-1.2-.5 2.2 4.9 1.1-.4 1.7-4.9-1.2-.6 2.7 5.5 1.3-.4 1.6z"
                        />
                    </g>
                </svg>
            )
        default:
            return null
    }
}
